import { useState, useEffect, useCallback } from 'react';
import { pillFill } from '@/lib/statusChart';
import { createPortal } from 'react-dom';
import { useAuth } from '@/contexts/AuthContext';
import api, { CLIENT_STATUSES, ADMIN_STATUSES, EDITOR_STATUSES, STATUS_COLORS, sanitizeProductLabel } from '@/lib/api';
import { LinkCheckChip, PullStateChip, useLinkChecks } from '@/components/LinkCheck';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import ChatThread from '@/components/ChatThread';
import { EditorSelect } from '@/components/EditorSelect';
import { FileRequestPanel } from '@/components/FileRequestPanel';
import { ClientFileRequest } from '@/components/ClientFileRequest';
import { AdminFileRequest } from '@/components/AdminFileRequest';
import VideoReview from '@/components/VideoReview';
import PhotoReview, { ExpectedPhotosChip } from '@/components/PhotoReview';
import { CullingDelivery } from '@/components/CullingDelivery';
import { isCullingOnly } from '@/lib/culling';
import { resolveProjectCategory } from '@/lib/mustHaves';
import { DOC_EDIT_NOTES_LABEL } from '@/lib/docEditNotes';
import FileUploadZone from '@/components/FileUploadZone';
import { SongApprovalEditor, SongApprovalClient } from '@/components/SongApproval';
import FootageLinks from '@/components/FootageLinks';
import FootagePullButton from '@/components/FootagePullButton';
import { AutoPrepCard, AutoPrepNotice } from '@/components/AutoPrepCard';
import EditorSwitchPanel from '@/components/EditorSwitchPanel';
import ErrorBoundary from '@/components/ErrorBoundary';
import DeadlineCountdown from '@/components/DeadlineCountdown';
import { formatEastern } from '@/lib/format';
import { activityFor } from '@/lib/clientActivity';
import { toast } from 'sonner';
import { X, Clock, FileText, Film, MessageSquare, Settings, Upload, User, Calendar, Check, Palette, Star, Lock, ChevronDown, Plus, Image, Maximize2, Pencil, Wand2, Flame, Scissors, Download, Package, Info, Copy, Archive, ArchiveRestore, Bot, Music, AlertTriangle, Ban, RotateCcw } from 'lucide-react';
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { clientStatusHelp } from '@/lib/clientStatusHelp';
import { keepsLink } from '@/lib/keepLink';
import KeepFootageLink from '@/components/KeepFootageLink';
import CullingInstructions from '@/components/CullingInstructions';
import ChangeProductModal from '@/components/ChangeProductModal';
import RevisionClockPanel from '@/components/RevisionClockPanel';
import { useConfirm } from '@/components/ConfirmDialog';
import { stopOf, askWords, ASK_TITLE, ASK_YES, ASK_NO } from '@/lib/handMove';
import { autoAdvanceChip } from '@/lib/autoAdvanceChip';
import { retentionNotice, restoreButton, filesRemoved } from '@/lib/retentionNotice';
import { ProjectTags } from '@/components/ProjectTags';
import { ReferredStudios } from '@/components/ReferralList';
import { NotesSection } from '@/components/NotesSection';
import { StyleProfileView } from '@/components/StyleProfileView';
import { RefundDialog } from '@/components/RefundDialog';
import { FrameStillsPanel } from '@/components/FrameStillsPanel';


// A PDF the server makes (the Invoice and Refund receipt buttons), saved as `name`.
async function downloadPdf(path, name, params) {
  const resp = await api.get(path, { params, responseType: 'blob' });
  const url = window.URL.createObjectURL(resp.data);
  const a = document.createElement('a'); a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
}

// ─── Expandable past review card with video preview ───
function ClientReviewCard({ review: r, viewerRole }) {
  const [expanded, setExpanded] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoError, setVideoError] = useState(false);

  const loadVideo = async () => {
    if (!r.final_video_key || videoUrl) return;
    setVideoLoading(true);
    try {
      const res = await api.post('/storage/presign-download', { key: r.final_video_key });
      setVideoUrl(res.data.download_url);
    } catch {
      setVideoError(true);
    } finally { setVideoLoading(false); }
  };

  const handleToggle = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && r.final_video_key && !videoUrl && !videoError) loadVideo();
  };

  return (
    <div className="rounded-xl border border-ug-border overflow-hidden transition-colors hover:border-ug-border">
      {/* Header — always visible, clickable */}
      <button onClick={handleToggle} className="w-full p-3 text-left flex items-center gap-2 hover:bg-ug-surface/50 transition-colors">
        {viewerRole !== 'editor' && <div className="flex gap-0.5 shrink-0">{[1,2,3,4,5].map(s => <Star key={s} className={`w-3 h-3 ${s <= r.rating ? 'text-ug-gold fill-gold-leaf' : 'text-slate-700'}`} />)}</div>}
        <span className="text-[11px] text-zinc-100 font-medium truncate flex-1">{r.project_title || 'Unknown Project'}</span>
        {r.final_version > 0 && <span className="font-sans text-[9px] font-semibold text-oxidized-teal shrink-0">V{r.final_version}</span>}
        <span className="font-sans text-[9px] font-semibold text-slate-600 shrink-0">{formatEastern(r.created_at, { dateOnly: true })}</span>
        <ChevronDown className={`w-3.5 h-3.5 text-slate-500 shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="px-3 pb-3 space-y-2 animate-stagger-1">
          {/* Feedback text */}
          <p className="text-[11px] text-zinc-100/70 leading-relaxed break-all">{r.feedback}</p>

          {/* Video preview */}
          {r.final_video_key && !videoError && (
            <div className="rounded-md overflow-hidden bg-black/40 border border-ug-border">
              {videoLoading ? (
                <div className="aspect-video flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-gold-leaf border-t-transparent rounded-full animate-spin" />
                </div>
              ) : videoUrl ? (
                <video src={videoUrl} controls className="w-full aspect-video bg-black" preload="metadata" />
              ) : (
                <div className="aspect-video flex items-center justify-center">
                  <span className="font-sans text-[10px] font-semibold text-slate-600">Loading video...</span>
                </div>
              )}
            </div>
          )}

          {/* Video deleted / unavailable */}
          {r.final_video_key && videoError && (
            <div className="p-3 rounded-xl bg-ug-raised border border-ug-border p-5 flex items-center gap-2">
              <Film className="w-4 h-4 text-slate-600 shrink-0" />
              <div>
                <p className="text-[11px] text-slate-400">Video no longer available</p>
                <p className="text-[9px] text-slate-600">The files were removed under our retention policy, 180 days after approval.</p>
              </div>
            </div>
          )}
          {!r.final_video_key && (
            <div className="p-2 rounded bg-ug-surface/50 border border-ug-border-subtle">
              <p className="text-[10px] text-slate-600 flex items-center gap-1.5"><Film className="w-3 h-3" /> No review video was uploaded for this project</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


/**
 * Editor pay for THIS job -- set at any point in its life, and EXPLAINED at
 * every point (operator, 2026-09-03: "I'm not going to know how this works at
 * any given time"). GET /projects/{id}/editor-pay reports the stage and every
 * number from the same code the write path uses, so what this panel says will
 * happen is what set_editor_pay_override does:
 *
 *   before delivery  the amount becomes the base when the first draft lands;
 *                    late fees still stack on top
 *   delivered/unpaid the base on the record is rewritten; penalty + fees stay
 *   paid             enter the new TOTAL; only the part above what was paid
 *                    becomes a new unpaid line. Lower than paid is refused.
 */
const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const STAGE_LABEL = { no_editor: 'No editor yet', before_delivery: 'Not delivered yet', unpaid: 'Delivered, unpaid', paid: 'Paid' };

function EditorPayControl({ project, onRefresh }) {
  const ov = project.editor_pay_override || null;
  const ovAmount = ov ? String(ov.amount) : '';
  const [amount, setAmount] = useState(ovAmount);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [pay, setPay] = useState(null);
  const [payErr, setPayErr] = useState('');
  // Re-seed the field when the drawer moves to another project or the stored
  // override changes; keyed on the primitive so exhaustive-deps is exact.
  useEffect(() => { setAmount(ovAmount); }, [project.id, ovAmount]);
  const loadState = useCallback(() => {
    setPayErr('');
    api.get(`/projects/${project.id}/editor-pay`)
      .then(r => setPay(r.data))
      .catch(e => setPayErr(e?.response?.data?.detail || 'Could not load pay details'));
  }, [project.id]);
  useEffect(() => { loadState(); }, [loadState, ovAmount]);
  const needs = !!project.editor_pay_needs_setting;
  const stage = pay?.stage || (needs ? 'before_delivery' : null);
  const rec = pay?.record || null;
  const extras = pay?.extras || [];
  const pendingExtra = extras.find(e => e.payment_status !== 'paid') || null;
  const covered = Number(pay?.covered || 0);
  const planned = pay?.planned || null;
  const typed = parseFloat(amount);
  const hasTyped = Number.isFinite(typed) && typed >= 0 && (!ov || Math.abs(typed - Number(ov.amount)) > 0.004);

  // What saving the typed amount would do, in the current stage -- the same
  // arithmetic the server applies, shown before the click.
  let preview = null;
  if (hasTyped && stage) {
    if (stage === 'paid') {
      const delta = Math.round((typed - covered) * 100) / 100;
      preview = delta < 0
        ? { tone: 'text-red-400', text: `Refused: ${money(covered)} has already been paid on this job. Add a negative manual adjustment to claw back.` }
        : delta === 0
          ? { tone: 'text-slate-400', text: `Already fully covered by the ${money(covered)} paid. Nothing further would be owed${pendingExtra ? '; the pending extra would be removed' : ''}.` }
          : { tone: 'text-oxidized-teal', text: `New total ${money(typed)}: the editor keeps the ${money(covered)} already paid and ${money(delta)} becomes a new unpaid line.` };
    } else if (stage === 'unpaid' && rec) {
      const owed = Math.max(0, Math.round((typed - rec.penalty - rec.fees) * 100) / 100);
      preview = { tone: 'text-oxidized-teal', text: `Base becomes ${money(typed)}; owed becomes ${money(owed)} (late penalty ${money(rec.penalty)}${rec.fees ? ` and fees ${money(rec.fees)}` : ''} stay).` };
    } else {
      preview = { tone: 'text-oxidized-teal', text: `${money(typed)} becomes the base pay when the first draft is delivered${planned && planned.amount !== typed ? ` (instead of the rate card's ${money(planned.amount)})` : ''}. Late fees still stack on top.` };
    }
  }

  const save = async () => {
    const n = parseFloat(amount);
    if (!Number.isFinite(n) || n < 0) { toast.error('Enter a valid amount'); return; }
    if (!reason.trim()) { toast.error('A reason is required'); return; }
    setSaving(true);
    try {
      const { data } = await api.put(`/projects/${project.id}/editor-pay`, { amount: n, reason: reason.trim() });
      toast.success(data?.message || 'Editor pay updated');
      setReason('');
      loadState();
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update editor pay');
    } finally { setSaving(false); }
  };

  const Row = ({ label, children, testid }) => (
    <div className="flex items-baseline justify-between gap-3 text-[10px]" data-testid={testid}>
      <span className="text-slate-500 shrink-0">{label}</span>
      <span className="text-right text-zinc-200">{children}</span>
    </div>
  );

  return (
    <div className={`rounded-md border p-3 ${needs ? 'border-amber-500/50 bg-amber-500/[0.05]' : 'border-ug-border bg-ug-surface/40'}`}
      data-testid="editor-pay-control">
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5">
          <h3 className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold/70">Editor Pay</h3>
          <TooltipProvider>
            <UITooltip delayDuration={150}>
              <TooltipTrigger asChild>
                <button type="button" className="tap-auto text-slate-500 hover:text-ug-gold" aria-label="How editor pay works" data-testid="editor-pay-help">
                  <Info className="w-3 h-3" strokeWidth={1.5} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" sideOffset={6} className="max-w-[320px] text-[11px] leading-snug">
                <p className="font-semibold mb-1">How editor pay works</p>
                <ul className="list-disc pl-3 space-y-1">
                  <li><span className="font-medium">Not delivered yet:</span> pay is planned from the editor's rate card. An amount saved here replaces it and becomes the base when the first draft lands. Late fees still stack on top.</li>
                  <li><span className="font-medium">Delivered, unpaid:</span> saving rewrites the base on the payment record. The late penalty and any fees stay as they are.</li>
                  <li><span className="font-medium">Paid:</span> enter the new total for the job. Only the part above what was already paid becomes a new unpaid line on the Payments page. Lowering below the paid amount is refused.</li>
                  <li>Changing the product later never touches an amount set here.</li>
                </ul>
              </TooltipContent>
            </UITooltip>
          </TooltipProvider>
        </div>
        <div className="flex items-center gap-1.5">
          {stage && (
            <span className={`tap-auto text-[8px] font-sans font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${stage === 'paid' ? 'bg-oxidized-teal/20 text-oxidized-teal' : stage === 'unpaid' ? 'bg-ug-gold/15 text-ug-gold' : 'bg-white/5 text-slate-400'}`}
              data-testid="editor-pay-stage">{STAGE_LABEL[stage] || stage}</span>
          )}
          {needs && (
            <span className="tap-auto text-[8px] font-sans font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500 text-obsidian"
              data-testid="editor-pay-needs-setting">Needs setting</span>
          )}
        </div>
      </div>

      {payErr && <p className="text-[10px] text-red-400 mb-2" data-testid="editor-pay-error">{payErr}</p>}
      {pay && (
        <div className="space-y-1 mb-2 pb-2 border-b border-ug-border/60" data-testid="editor-pay-state">
          {stage === 'no_editor' && (
            <p className="text-[10px] text-slate-400">No editor assigned yet, so there is no rate card to plan from. An amount saved here is kept and applies when the first draft lands.</p>
          )}
          {planned && (
            <Row label={ov ? 'Rate card would pay' : 'Planned from rate card'} testid="editor-pay-planned">
              {needs && planned.amount === 0
                ? <span className="text-amber-300">nothing: custom order, no rate applies</span>
                : <>
                    <span className={ov ? 'line-through text-slate-500' : 'font-medium text-zinc-100'}>{money(planned.amount)}</span>
                    {planned.breakdown.length > 0 && (
                      <span className="block text-[9px] text-slate-500">{planned.breakdown.map(b => `${b.product} ${money(b.rate)}`).join(' + ')}{pay.editor_name ? ` for ${pay.editor_name}` : ''}</span>
                    )}
                  </>}
            </Row>
          )}
          {ov && (
            <Row label="Set on this job" testid="editor-pay-override">
              <span className="font-medium text-zinc-100">{money(ov.amount)}</span>
              <span className="block text-[9px] text-slate-500">by {ov.set_by_name || 'admin'}{ov.set_at ? ` on ${new Date(ov.set_at).toLocaleDateString()}` : ''}{ov.reason ? `: ${ov.reason}` : ''}{stage === 'before_delivery' || stage === 'no_editor' ? '. Applies at first-draft delivery.' : ''}</span>
            </Row>
          )}
          {rec && (
            <Row label={rec.payment_status === 'paid' ? 'Paid' : 'Owed now'} testid="editor-pay-record">
              <span className="font-medium text-zinc-100">{money(rec.amount_owed)}</span>
              <span className="block text-[9px] text-slate-500">
                base {money(rec.base_pay)}
                {rec.penalty > 0 ? ` minus late penalty ${money(rec.penalty)} (${rec.days_late} day${rec.days_late === 1 ? '' : 's'} at ${money(rec.late_fee_rate)})` : ' with no late penalty'}
                {rec.fees > 0 ? ` minus fees ${money(rec.fees)}` : ''}
                {rec.payment_status === 'paid' && rec.paid_at ? `. Paid ${new Date(rec.paid_at).toLocaleDateString()}` : ''}
              </span>
            </Row>
          )}
          {extras.map(e => (
            <Row key={e.id} label={e.payment_status === 'paid' ? 'Extra paid' : 'Extra owed'} testid="editor-pay-extra">
              <span className={`font-medium ${e.payment_status === 'paid' ? 'text-zinc-100' : 'text-ug-gold'}`}>{money(e.amount)}</span>
              <span className="block text-[9px] text-slate-500">{e.reason || 'raised after payment'}{e.payment_status === 'paid' && e.paid_at ? `. Paid ${new Date(e.paid_at).toLocaleDateString()}` : ' (shows on the Payments page as its own line)'}</span>
            </Row>
          ))}
          <p className="text-[10px] text-slate-400 pt-1" data-testid="editor-pay-what-saving-does">
            {stage === 'paid'
              ? <>Saving here sets the job's <span className="text-zinc-200">new total</span>. The {money(covered)} already paid stays paid; only the difference above it becomes a new unpaid line.</>
              : stage === 'unpaid'
                ? <>Saving here rewrites the base on the delivered record. The late penalty and fees stay.</>
                : <>Saving here sets the base pay for when the first draft is delivered. Late fees still stack on top.</>}
          </p>
        </div>
      )}

      <div className="flex items-center gap-2">
        <span className="font-sans text-xs text-slate-400">$</span>
        <Input type="number" inputMode="decimal" min={0} step="0.01" value={amount} onChange={e => setAmount(e.target.value)}
          placeholder={stage === 'paid' ? 'New total' : 'Amount'} data-testid="editor-pay-amount"
          className="bg-ug-surface border-ug-border text-zinc-100 h-8 text-sm w-28 placeholder:text-white/40" />
        <Input value={reason} onChange={e => setReason(e.target.value)} maxLength={200}
          placeholder="Reason (required)" data-testid="editor-pay-reason"
          className="bg-ug-surface border-ug-border text-zinc-100 h-8 text-sm flex-1 placeholder:text-white/40" />
        <button type="button" onClick={save} disabled={saving || (preview && preview.tone === 'text-red-400')} data-testid="editor-pay-save"
          className="tap-auto px-3 h-8 rounded bg-gold-leaf text-obsidian font-sans text-[10px] font-semibold uppercase tracking-wider hover:bg-gold-leaf/85 disabled:opacity-50">
          {saving ? 'Saving' : 'Save'}
        </button>
      </div>
      {preview
        ? <p className={`text-[9px] mt-1.5 ${preview.tone}`} data-testid="editor-pay-preview">{preview.text}</p>
        : <p className="text-[9px] text-slate-600 mt-1.5">Type an amount to see exactly what saving it would do.</p>}
    </div>
  );
}

export default function ProjectPanel({ project, onClose, onRefresh, defaultTab, expandChat, onChatRead, persistedTab, onTabChange, onUploading: onUploadingProp }) {
  const { user } = useAuth();
  const [refundMode, setRefundMode] = useState(null);   // 'refund' | 'cancel' | null (components/RefundDialog.js)
  // Shared scoped fallback for tab-level ErrorBoundaries — identical text + visual style across all tabs
  const tabErrorFallback = (
    <div className="rounded-xl bg-ug-raised border border-ug-border p-5 text-center">
      <p className="text-[12px] text-slate-400">This tab encountered an error.</p>
      <p className="text-[10px] text-slate-600 mt-1">Other tabs should still work — please try refreshing.</p>
    </div>
  );
  // ARCHIVING LOCKS NOTHING (Ryan, 2026-09-29): "the only real reason I want to archive is to
  // speed up how fast the main board loads", so an archived job works exactly as it did on the
  // board. `archived` (the board flag, app/archive.py) used to be a work lock for staff and
  // editors here: no new versions, the editor chat read-only, and Add Edit, Change edit type and
  // Record External Payment hidden. A client's or editor's own archive (`my_archived`) was always
  // just a list filter.
  const ownArchive = user.role === 'client' || user.role === 'editor';
  const [archiving, setArchiving] = useState(false);
  const toggleOwnArchive = async () => {
    if (archiving) return;
    setArchiving(true);
    try {
      await api.put(`/projects/${project.id}/${project.my_archived ? 'unarchive' : 'archive'}`);
      toast.success(project.my_archived ? 'Back on your list' : 'Moved to your Archived list');
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update');
    } finally {
      setArchiving(false);
    }
  };
  const isPending = user.role === 'editor' && (project?.editor_status_id === -1 || project?.editor_status_id === '-1');
  const isLocked = isPending;
  const [aaSaving, setAaSaving] = useState(false);
  const [esSaving, setEsSaving] = useState(false);
  const toggleEditorSwitch = async () => {
    const disabled = !project.editor_switch?.stopped;
    setEsSaving(true);
    try {
      await api.put(`/projects/${project.id}/editor-switch`, { disabled });
      toast.success(disabled ? 'Switch offer stopped for this project' : 'Switch offer allowed again');
      onRefresh?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not change the switch offer'); }
    finally { setEsSaving(false); }
  };
  const [sdSaving, setSdSaving] = useState(false);
  const toggleSongDeadline = async () => {
    const disabled = !project.song_deadline_disabled;
    setSdSaving(true);
    try {
      await api.put(`/projects/${project.id}/song-deadline`, { disabled });
      toast.success(disabled ? 'Song deadline stopped for this project' : 'Song deadline allowed again');
      onRefresh?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not change the song deadline'); }
    finally { setSdSaving(false); }
  };
  const toggleAutoAdvance = async () => {
    const disabled = !project.auto_advance_disabled;
    setAaSaving(true);
    try {
      await api.put(`/projects/${project.id}/auto-advance`, { disabled });
      toast.success(disabled ? 'Auto-advance stopped for this project' : 'Auto-advance allowed again');
      onRefresh?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not change auto-advance'); }
    finally { setAaSaving(false); }
  };
  // Keep files (admin): the one lever retention has since archiving stopped exempting files
  // (operator ruling 2026-09-21). Turning it OFF puts the files back on the deletion clock, so
  // that direction asks first; turning it on never needs to.
  const [fkSaving, setFkSaving] = useState(false);
  const toggleFilesKeep = async () => {
    const keep = !project.files_keep;
    if (!keep) {
      const ok = await confirm({
        title: 'Put these files back on the deletion clock?',
        message: 'This project\'s files are being kept. If you switch that off, they go back on the normal schedule: deleted 180 days after the client approved, with a warning email 7 days before. This is recorded with your name.',
        confirmLabel: 'Back on the clock', cancelLabel: 'Keep the files', destructive: true });
      if (!ok) return;
    }
    setFkSaving(true);
    try {
      await api.put(`/projects/${project.id}/files-keep`, { keep });
      toast.success(keep ? 'Files kept: this project is never auto-deleted' : 'Files back on the 180-day deletion clock');
      onRefresh?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not change file keeping'); }
    finally { setFkSaving(false); }
  };
  // Restore files (admin/staff): the way back for a project soft-deleted under the retention
  // policy, while its 30-day window is open. The safe direction: no confirm. The route writes
  // the history line itself, so nothing is recorded twice.
  const [restoring, setRestoring] = useState(false);
  const restore = restoreButton(project, user.role);
  const retention = retentionNotice(project);
  const restoreFiles = async () => {
    setRestoring(true);
    try {
      const r = await api.post(`/admin/retention/${project.id}/restore`);
      toast.success(`Files restored: ${r.data?.n_files ?? 0} file(s) back to normal`);
      onRefresh?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not restore the files'); }
    finally { setRestoring(false); }
  };
  // What the button says is decided in lib/autoAdvanceChip.js, with the states written out and
  // tested there: the label states the MODE (live, watching only, or nothing at all), never just
  // whether this one project is stopped.
  const aa = autoAdvanceChip(project, user.role);
  // THE CLIENT CONVERSATION IS NOT PART OF THE LOCK.
  // Archiving freezes the WORK — no new versions, no status changes, no edits to a
  // finished job. It was also freezing the client chat, which is a different kind
  // of thing: a client asking "can I get the raw files for this one?" months later
  // is normal, and refusing to answer was never a policy anyone chose — it fell out
  // of one blanket flag covering both. Archived client threads are listed in All
  // Chats and count toward unread, so being able to see the question but not answer
  // it left the only route as unarchive -> reply -> re-archive.
  // `isPending` still applies (an editor with an unassigned project has no business
  // messaging the client yet). Since 2026-09-29 archiving locks nothing at all, the internal
  // thread included: see the note on `isLocked`.
  const isClientChatLocked = isPending;
  const [files, setFiles] = useState([]);
  const [editors, setEditors] = useState([]);
  const [uploadsInProgress, setUploadsInProgress] = useState(false);
  const [showManualPayModal, setShowManualPayModal] = useState(false);
  const [showChangeProduct, setShowChangeProduct] = useState(false);
  const [manualPayNote, setManualPayNote] = useState('');
  const [manualPayProcessing, setManualPayProcessing] = useState(false);
  const [idCmdCopied, setIdCmdCopied] = useState(false);
  const { data: drawerLinkChecks } = useLinkChecks((user.role === 'admin' || user.role === 'staff') ? project?.id : null);
  const handleUploading = (v) => { setUploadsInProgress(v); if (onUploadingProp) onUploadingProp(v); };
  const confirm = useConfirm();
  // Culling Only fork (Phase 3 client, Phase 4 admin/staff/editor): the job
  // delivers an NLE project file, so NOBODY gets the video review UI — no empty
  // player, no full screen, no timestamped notes / compare, no footage analysis
  // (all of which live inside VideoReview and therefore disappear with the tab).
  // Clients get confirm + download; admin/staff/editor get upload + the file list.
  const isCullingProject = isCullingOnly(project);
  const isCullingClient = user.role === 'client' && isCullingProject;
  // Culling instructions tab: video culling jobs, now for everyone who works the
  // job (admin/staff/editor). Clients never see the editor spec.
  const isVideoCullingJob = ['editor', 'admin', 'staff'].includes(user.role)
    && project.project_type !== 'photo'
    && (isCullingProject || (project.product_name || '').toLowerCase().includes('culling'));
  // Who reads the client's notes in the one Notes section (components/NotesSection.js) rather than the old blocks.
  const notesInSection = ['editor', 'admin', 'staff'].includes(user.role);
  // Tab persistence across project switches
  const availableTabs = project.project_type === 'photo'
    ? ['photos', 'files', 'notes', 'status', 'feedback', 'profile', 'activity']
    : isCullingClient
    ? ['delivery', 'files', 'notes', 'feedback', 'profile', 'activity']
    : isCullingProject
    ? ['delivery', 'files', 'notes', ...(isVideoCullingJob ? ['culling'] : []), 'status', 'feedback', 'profile', 'activity']
    : ['review', 'files', 'notes', ...(isVideoCullingJob ? ['culling'] : []), 'status', 'feedback', 'profile', 'activity'];
  const defaultForType = project.project_type === 'photo' ? 'photos' : isCullingProject ? 'delivery' : 'review';
  const [tab, setTabInternal] = useState(() => {
    if (persistedTab && availableTabs.includes(persistedTab)) return persistedTab;
    return defaultForType;
  });
  const setTab = (t) => { setTabInternal(t); if (onTabChange) onTabChange(t); };
  // When project changes, use persisted tab if valid for new project type
  useEffect(() => {
    if (persistedTab && availableTabs.includes(persistedTab)) {
      setTabInternal(persistedTab);
    } else {
      setTabInternal(defaultForType);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.id]);
  const [fullscreen, setFullscreen] = useState(null);
  const [fullscreenInitialTime, setFullscreenInitialTime] = useState(0);
  // undefined = not fetched yet (drawer just opened), null = fetched and none. The Profile
  // tab used to print "No style profile set up" during the fetch, which read as a fact.
  const [clientProfile, setClientProfile] = useState(undefined);
  const [clientData, setClientData] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [clientReviews, setClientReviews] = useState([]);
  const [newRating, setNewRating] = useState(5);
  const [newFeedback, setNewFeedback] = useState('');
  const [editingDeliveryLink, setEditingDeliveryLink] = useState(false);
  const [deliveryLinkVal, setDeliveryLinkVal] = useState('');
  const [aiToolUrl, setAiToolUrl] = useState('');
  const [pmOnDuty, setPmOnDuty] = useState(null); // {user_id, name} | null — chip in internal chat header
  const [editingTitle, setEditingTitle] = useState(false);
  const [editingTitleVal, setEditingTitleVal] = useState('');

  useEffect(() => {
    if (project) {
      api.get(`/projects/${project.id}/files`).then(r => setFiles(r.data)).catch(() => {});
      api.get(`/projects/${project.id}/reviews`).then(r => setReviews(r.data)).catch(() => {});
      if (user.role !== 'client') {
        api.get('/settings/ai_tool_url').then(r => setAiToolUrl(r.data?.value || '')).catch(() => {});
        // "PM on duty" — piggybacks the same page-load fetch pattern. Chip only
        // renders for editors on the internal chat header (see ChatThread props).
        api.get('/settings/pm_on_duty').then(r => {
          const v = r.data?.value;
          setPmOnDuty(v && typeof v === 'object' && v.user_id ? v : null);
        }).catch(() => setPmOnDuty(null));
      }
      setClientProfile(undefined);
      // The Style Profile this project is made to: its own brand's, for every role that opens it
      // (GET /projects/{id}/style-profile, backend/app/project_style_profile.py).
      if (project.client_id) {
        api.get(`/projects/${project.id}/style-profile`).then(r => setClientProfile(r.data)).catch(() => setClientProfile(null));
      } else {
        setClientProfile(null);
      }
      if (user.role === 'admin' || user.role === 'staff') {
        api.get('/users/editors').then(r => setEditors(r.data)).catch(() => {});
        if (project.client_id) {
          api.get('/users').then(r => { const c = r.data.find(u => u.id === project.client_id); if (c) setClientData(c); }).catch(() => {});
          api.get(`/reviews/by-client/${project.client_id}`).then(r => setClientReviews(r.data)).catch(() => {});
        }
      }
      if (user.role === 'editor' && project.client_id) {
        api.get(`/reviews/by-client/${project.client_id}`).then(r => setClientReviews(r.data)).catch(() => {});
      }
      // Only override tab if defaultTab is explicitly set (e.g., from action item click)
      // Otherwise, let the persistence useEffect handle it
      if (defaultTab) {
        setTab(defaultTab);
      }
      setEditingTitle(false);
    }
  }, [project?.id, user.role, project?.client_id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!project) return null;

  // A move off Project Requested with no footage the portal can pull is refused in words (lib/handMove.js). Admin is
  // asked, and may make it anyway; anyone else is told what to do instead.
  const handleAdminStatus = async (val, force = false) => {
    try {
      await api.put(`/projects/${project.id}/admin-status`, force ? { status_id: parseInt(val), force: true } : { status_id: parseInt(val) });
      toast.success('Status updated');
      onRefresh();
    } catch (e) {
      const stop = stopOf(e);
      if (!stop) { toast.error('Failed to update'); return; }
      if (!force && stop.canOverride && await confirm({ title: ASK_TITLE, message: askWords(stop), confirmLabel: ASK_YES, cancelLabel: ASK_NO })) {
        await handleAdminStatus(val, true);
        return;
      }
      if (!stop.canOverride) toast.error(stop.message, { duration: 12000 });
      onRefresh();
    }
  };

  const handleEditorStatus = async (val) => {
    try {
      await api.put(`/projects/${project.id}/editor-status`, { status_id: parseInt(val) });
      toast.success('Status updated');
      onRefresh();
    } catch { toast.error('Failed to update'); }
  };

  const handleAssignEditor = async (editorId) => {
    try {
      await api.put(`/projects/${project.id}/assign-editor`, { editor_id: editorId });
      toast.success('Editor assigned');
      onRefresh();
    } catch { toast.error('Failed to assign'); }
  };

  const handleFileUpload = async (fileType) => {
    const filename = prompt(`Enter filename for ${fileType}:`);
    if (!filename) return;
    try {
      await api.post(`/projects/${project.id}/files`, { filename, file_type: fileType, version_number: project.current_version || null });
      toast.success('File added');
      api.get(`/projects/${project.id}/files`).then(r => setFiles(r.data)).catch(() => setFiles([]));
      onRefresh();
    } catch { toast.error('Failed'); }
  };

  const handleUpdateProject = async (field, value) => {
    try { await api.put(`/projects/${project.id}`, { [field]: value }); toast.success('Updated'); onRefresh(); } catch { toast.error('Failed'); }
  };

  const handleSubmitReview = async () => {
    if (!newFeedback.trim()) { toast.error('Please add feedback'); return; }
    try {
      await api.post(`/projects/${project.id}/reviews`, { rating: newRating, feedback: newFeedback, version_number: project.current_version });
      toast.success('Feedback submitted — your editor will see this');
      setNewFeedback(''); setNewRating(5);
      api.get(`/projects/${project.id}/reviews`).then(r => setReviews(r.data)).catch(() => {});
    } catch { toast.error('Failed'); }
  };

  // Header badge: show the status track appropriate to the viewer's role
  const headerStatusId = user.role === 'editor' ? project.editor_status_id
    : user.role === 'client' ? project.client_status_id
    : project.admin_status_id;
  const headerStatusMap = user.role === 'editor' ? EDITOR_STATUSES
    : user.role === 'client' ? CLIENT_STATUSES
    : ADMIN_STATUSES;
  const statusColor = pillFill(STATUS_COLORS[headerStatusId] || '#94A3B8');   // white ink on it: lib/statusChart pillFill

  return (
    <div className="fixed inset-0 z-40 lg:inset-y-0 lg:left-auto lg:right-0 lg:w-[440px] panel-border-glow glass-panel flex flex-col panel-enter overflow-hidden bg-ug-raised" style={{ top: '32px', overflowWrap: 'break-word', wordBreak: 'break-word' }} data-testid="project-panel">
      {/* Panel Header */}
      <div className="px-5 pt-5 pb-3" data-testid="drawer-header">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              {editingTitle ? (
                <form onSubmit={async (e) => { e.preventDefault(); const val = editingTitleVal.trim(); if (!val) { toast.error('Title cannot be empty'); return; } await handleUpdateProject('title', val); setEditingTitle(false); }} className="flex items-center gap-1.5 flex-1 min-w-0">
                  <input value={editingTitleVal} onChange={e => setEditingTitleVal(e.target.value)} autoFocus onKeyDown={e => e.key === 'Escape' && setEditingTitle(false)}
                    className="font-sans font-bold text-lg text-zinc-100 bg-ug-surface border border-ug-gold/30 rounded px-2 py-0.5 outline-none focus:border-ug-gold/60 flex-1 min-w-0" />
                  <Button size="sm" type="submit" className="bg-gold-leaf text-obsidian hover:bg-gold-leaf/90 h-7 text-[9px] font-mono px-2">Save</Button>
                  <Button size="sm" type="button" variant="ghost" onClick={() => setEditingTitle(false)} className="text-slate-500 h-7 text-[9px] font-mono px-1">Cancel</Button>
                </form>
              ) : (
                <h2 className="font-sans font-bold text-lg text-zinc-100 truncate">{project.title}</h2>
              )}
              {!editingTitle && user.role !== 'editor' && <button onClick={() => { setEditingTitle(true); setEditingTitleVal(project.title || ''); }} className="text-slate-600 hover:text-ug-gold transition-colors shrink-0" title="Edit title"><Pencil className="w-3 h-3" /></button>}
              {project.project_type === 'photo' ? <Image className="w-3.5 h-3.5 text-ug-gold/50 shrink-0" /> : <Film className="w-3.5 h-3.5 text-ug-gold/50 shrink-0" />}
              {project.two_edits && (
                <div className="shrink-0 self-center h-[18px] overflow-hidden flex items-start rounded-sm bg-gold-leaf/15 border border-gold-leaf/25">
                  <span className="font-sans text-[9px] font-semibold text-ug-gold whitespace-nowrap px-2 leading-[18px]" data-testid="drawer-double-edit-badge">
                    {project.two_edits_label || '2x'}
                  </span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 mt-1">
              <span className="font-sans text-[10px] font-semibold text-ug-gold/60 uppercase tracking-wider">{project.brand_name || project.studio_name || project.client_name}</span>
              {project.editor_name && user.role !== 'client' && (
                <>
                  <span className="text-slate-600 text-[10px]">/</span>
                  <span className="font-sans text-[10px] font-semibold text-oxidized-teal/60 uppercase tracking-wider">{project.editor_name}</span>
                </>
              )}
            </div>
            {(project.brand_name || project.studio_name) && (
              <span className="font-sans text-[9px] font-semibold text-slate-500">{project.client_name}</span>
            )}
            {user.role === 'admin' && project.client_footage_avg && (
              <span className={`font-sans text-[8px] font-bold px-1.5 py-0.5 rounded-full ${project.client_footage_avg >= 4 ? 'bg-oxidized-teal/10 text-oxidized-teal' : project.client_footage_avg >= 3 ? 'bg-ug-gold/10 text-ug-gold' : 'bg-orange-400/10 text-orange-400'}`} data-testid="client-footage-avg">
                Footage Avg: {project.client_footage_avg}/5 ({project.client_footage_count} project{project.client_footage_count !== 1 ? 's' : ''})
              </span>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={async () => {
            if (uploadsInProgress) {
              const ok = await confirm({ title: 'Files are still uploading', message: 'If you close now, uploads will continue in the background but you won\'t see the progress.', confirmLabel: 'Close Anyway', cancelLabel: 'Keep Open', destructive: true });
              if (!ok) return;
            }
            onClose();
          }} data-testid="close-panel-btn"
            className="text-slate-500 hover:text-zinc-100 h-7 w-7 p-0 -mt-1 -mr-1">
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* A cancelled order (server.py cancel_order). Everyone who can open it is told; only the team sees the
            money and the reason, which the server keeps from everyone else (STAFF_VIEW_FIELDS). */}
        {project.cancelled && (() => {
          const team = user.role === 'admin' || user.role === 'staff';
          const back = Number(project.refunded_amount || 0);
          const day = project.cancelled_at ? new Date(project.cancelled_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
          return (
            <div data-testid="drawer-cancelled-banner" data-tone="warning"
              className="mb-2 rounded-lg border border-amber-500/40 bg-amber-500/[0.06] px-3 py-2 flex items-start gap-2 text-[11px] text-amber-300">
              <Ban className="w-3.5 h-3.5 shrink-0 mt-px" />
              <span className="flex-1 leading-relaxed">
                {team
                  ? <>Cancelled{day ? ` ${day}` : ''}{project.cancelled_by_name ? ` by ${project.cancelled_by_name}` : ''}. {back > 0 ? `$${back.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} refunded in all.` : 'No refund.'}{project.cancel_reason ? <span className="text-slate-400"> {project.cancel_reason}</span> : null}</>
                  : "This job was cancelled at the client's request. No further work is needed."}
              </span>
              {user.role === 'admin' && (
                <button type="button" data-testid="drawer-uncancel"
                  onClick={async () => { try { await api.post(`/projects/${project.id}/uncancel`); toast.success('Back on the board. Refunds already made stay recorded.'); onRefresh(); } catch (e) { toast.error(e?.response?.data?.detail || 'Could not undo'); } }}
                  className="tap-auto shrink-0 text-[10px] font-semibold uppercase tracking-wider text-slate-400 hover:text-zinc-100 underline underline-offset-2">Undo</button>
              )}
            </div>
          );
        })()}
        {!project.cancelled && Number(project.refunded_amount || 0) > 0 && (user.role === 'admin' || user.role === 'staff') && (
          <div data-testid="drawer-refunded-note" className="mb-2 text-[11px] text-slate-400">
            ${Number(project.refunded_amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} refunded on this project. Revenue counts what the client kept.
          </div>
        )}

        {/* Status + Meta row */}
        <div className="flex items-center gap-2 flex-wrap">
          <Badge className="font-sans text-[10px] font-semibold uppercase tracking-wider border-0 px-2.5 py-0.5"
            style={{ backgroundColor: statusColor, color: '#fff' }}>
            {headerStatusMap[headerStatusId] || 'Unknown'}
          </Badge>
          {/* Part of a new client's FIRST ORDER: everything they bought on that first trip, and
              only for clients who did not come across from the old portal (app/first_order.py,
              operator 2026-09-19). DERIVED on the server, never typed, so it cannot go stale the
              way a hand-set label would. Staff only, like the tags. */}
          {project.first_order && (user.role === 'admin' || user.role === 'staff') && (
            <span data-testid="first-order-chip"
              title="Part of this client's first order with us, and they are new (not carried over from the old portal)."
              className="tap-auto font-sans text-[9px] font-semibold uppercase tracking-wider text-oxidized-teal border border-oxidized-teal/40 bg-oxidized-teal/[0.07] rounded-full px-2 py-0.5">
              First order
            </span>
          )}
          {/* Internal tags, and the plus that adds one. Renders nothing unless the server sent
              `tags`, which it does for admin and staff only (components/ProjectTags.js). */}
          <ProjectTags project={project} canEdit={user.role === 'admin' || user.role === 'staff'}
            isAdmin={user.role === 'admin'} onChange={onRefresh} />
          {/* What the status MEANS, on the status itself. A label is a name, not
              an explanation — "Processing" tells a client we have their order but
              not that we are downloading their footage right now. The sentence
              comes from lib/clientStatusHelp.js, the same source as the paragraph
              further down, so the pill and the prose cannot disagree.
              Client-only: the pill shows the admin or editor track for everyone
              else, and this copy describes neither. */}
          {user.role === 'client' && clientStatusHelp(project.client_status_id, isCullingProject, filesRemoved(project), project.keep_footage_link) && (
            <TooltipProvider>
              <UITooltip delayDuration={150}>
                <TooltipTrigger asChild>
                  <button type="button" tabIndex={0} data-testid="client-status-help"
                    className="tap-auto w-5 h-5 shrink-0 rounded flex items-center justify-center text-slate-500 hover:text-ug-gold cursor-help focus:outline-none focus-visible:ring-1 focus-visible:ring-ug-gold/60"
                    aria-label="What this status means">
                    <Info className="w-3.5 h-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" sideOffset={6} className="max-w-[300px] text-[11px] leading-snug">
                  {clientStatusHelp(project.client_status_id, isCullingProject, filesRemoved(project), project.keep_footage_link)}
                </TooltipContent>
              </UITooltip>
            </TooltipProvider>
          )}
          {project.current_version > 0 && (
            <Badge className="font-sans text-[10px] font-semibold bg-oxidized-teal/15 text-oxidized-teal border border-oxidized-teal/20">V{project.current_version}</Badge>
          )}
          <LinkCheckChip projectId={project.id} role={user.role} />
          <PullStateChip data={drawerLinkChecks} role={user.role} />
          {user.role !== 'editor' && (
            <Badge className={`font-sans text-[9px] font-semibold border-0 px-2 py-0.5 ${project.payment_status === 'paid' ? 'bg-oxidized-teal/15 text-oxidized-teal' : 'bg-ug-gold/15 text-ug-gold'}`}>
              {project.payment_status === 'paid' ? (project.manual_payment ? 'PAID (manual)' : 'PAID') : 'UNPAID'}
            </Badge>
          )}
          {user.role !== 'editor' && project.payment_status === 'paid' && (
            <button onClick={async (e) => {
              e.stopPropagation();
              try {
                await downloadPdf(`/invoices/generate/${project.id}`, `invoice-${project.title || project.id}.pdf`);
              } catch (err) {
                const msg = err?.response?.status === 400 ? 'Please complete your billing details in your Profile to download an invoice.' : 'Could not generate invoice.';
                toast.error(msg);
              }
            }} data-testid="download-invoice-btn"
              className="tap-auto h-5 font-sans text-[8px] font-semibold uppercase tracking-wider text-oxidized-teal/70 hover:text-oxidized-teal border border-oxidized-teal/20 hover:border-oxidized-teal/40 px-2 py-0.5 rounded transition-colors flex items-center gap-1">
              <Download className="w-2.5 h-2.5" /> Invoice
            </button>
          )}
          {/* A receipt for each refund on this project (app/refund_receipt.py), the one the client's refund email
              carried. The team only: `refunds` is kept from everyone else (STAFF_VIEW_FIELDS). */}
          {(user.role === 'admin' || user.role === 'staff') && project.payment_status === 'paid' && (project.refunds || []).map((r, i, all) => {
            const day = all.length > 1 && r.at ? new Date(r.at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
            return (
              <button key={r.stripe_refund_id || r.id} onClick={async (e) => {
                e.stopPropagation();
                try {
                  await downloadPdf(`/invoices/refund-receipt/${project.id}`,
                    `refund-receipt-${project.title || project.id}${day ? `-${day}` : ''}.pdf`, { refund: r.stripe_refund_id || r.id });
                } catch {
                  toast.error('Could not make the refund receipt.');
                }
              }} data-testid={`download-refund-receipt-btn-${i}`}
                className="tap-auto h-5 font-sans text-[8px] font-semibold uppercase tracking-wider text-oxidized-teal/70 hover:text-oxidized-teal border border-oxidized-teal/20 hover:border-oxidized-teal/40 px-2 py-0.5 rounded transition-colors flex items-center gap-1">
                <Download className="w-2.5 h-2.5" /> Refund receipt{day ? ` ${day}` : ''}
              </button>
            );
          })}
          {user.role === 'admin' && project.payment_status !== 'paid' && !project.deleted && (
            <button onClick={() => setShowManualPayModal(true)} data-testid="record-external-payment-btn"
              className="tap-auto h-5 font-sans text-[8px] font-semibold uppercase tracking-wider text-ug-gold/60 hover:text-ug-gold border border-ug-gold/20 hover:border-ug-gold/40 px-2 py-0.5 rounded transition-colors flex items-center">
              Record External Payment
            </button>
          )}
          {/* Auto-advance stop flag (admin/staff): "not this one". Shown while the machine could
              still act on the project (Project Requested) or while it is stopped. The word it
              shows is the MODE's, not the flag's: lib/autoAdvanceChip.js. */}
          {aa.shows && (
            <button onClick={toggleAutoAdvance} disabled={aaSaving} data-testid="auto-advance-toggle"
              data-mode={aa.mode}
              title={aa.title}
              className={`tap-auto flex items-center gap-1 text-[9px] font-sans font-semibold uppercase tracking-wider border rounded-full px-2 py-0.5 transition-colors disabled:opacity-50 ${
                aa.stopped ? 'text-ug-status-red border-ug-status-red/40 hover:border-ug-status-red' : 'text-slate-500 hover:text-ug-gold border-ug-border hover:border-gold-leaf/30'}`}>
              <Bot className="w-3 h-3" />
              {aa.label}
            </button>
          )}
          {/* Keep files (admin only). Reads as a sentence either way: what will happen to the files
              and, once kept, who decided. Same shape as the stop switches beside it. */}
          {user.role === 'admin' && (
            <button onClick={toggleFilesKeep} disabled={fkSaving} data-testid="files-keep-toggle"
              data-kept={project.files_keep ? 'true' : 'false'}
              title={project.files_keep
                ? `This project's files are kept: never auto-deleted${project.files_keep_by ? ` (kept by ${project.files_keep_by})` : ''}. Click to put them back on the 180-day deletion clock. You will be asked first.`
                : 'Files are deleted 180 days after the client approves, with a warning email 7 days before. Click to keep this project\'s files instead: never auto-deleted.'}
              className={`tap-auto flex items-center gap-1 text-[9px] font-sans font-semibold uppercase tracking-wider border rounded-full px-2 py-0.5 transition-colors disabled:opacity-50 ${
                project.files_keep ? 'text-oxidized-teal border-oxidized-teal/40 hover:border-oxidized-teal' : 'text-slate-500 hover:text-ug-gold border-ug-border hover:border-gold-leaf/30'}`}>
              <Lock className="w-3 h-3" />
              {project.files_keep ? 'Files kept, never auto-deleted' : 'Keep files'}
            </button>
          )}
          {restore && (
            <button onClick={restoreFiles} disabled={restoring} data-testid="restore-files-btn" title={restore.title}
              className="tap-auto flex items-center gap-1 text-[9px] font-sans font-semibold uppercase tracking-wider border rounded-full px-2 py-0.5 transition-colors disabled:opacity-50 text-ug-gold border-ug-gold/40 hover:border-ug-gold">
              <ArchiveRestore className="w-3 h-3" />
              {restore.label}
            </button>
          )}
          {/* Song deadline stop (admin/staff): shown while a TIMED proposal is live on a
              pre-draft project, or while it is stopped. Honoured in every mode.
              An untimed set (no delivery date, so no song deadline: operator ruling
              2026-09-16) has no clock to stop, and this button's tooltip states that the
              24-hour rule applies, so showing it there would say something untrue. */}
          {(user.role === 'admin' || user.role === 'staff') && (project.song_wait?.timed || project.song_deadline_disabled) && (
            <button onClick={toggleSongDeadline} disabled={sdSaving} data-testid="song-deadline-toggle"
              title={project.song_deadline_disabled
                ? `The 24-hour song rule is stopped on this project${project.song_deadline_disabled_by ? ` (by ${project.song_deadline_disabled_by})` : ''}. Click to allow it again.`
                // Said per mode, never assumed (operator ruling 2026-09-16): the mode comes from the
                // server with song_wait and can be switched back to shadow at any time.
                : ({
                    live: 'The 24-hour song rule applies: if the client answers late, this project\'s dates move. Click to stop it for this project.',
                    shadow: 'The 24-hour song rule is in shadow: nothing moves on this project, and the song deadline page records what would have. Click to stop it for this project.',
                    off: 'The 24-hour song rule is switched off: nothing is checked. Click to stop it for this project as well.',
                  })[project.song_wait?.mode] || 'Click to stop the 24-hour song rule on this project.'}
              className={`tap-auto flex items-center gap-1 text-[9px] font-sans font-semibold uppercase tracking-wider border rounded-full px-2 py-0.5 transition-colors disabled:opacity-50 ${
                project.song_deadline_disabled ? 'text-ug-status-red border-ug-status-red/40 hover:border-ug-status-red' : 'text-slate-500 hover:text-ug-gold border-ug-border hover:border-gold-leaf/30'}`}>
              <Music className="w-3 h-3" />
              {project.song_deadline_disabled ? 'Song rule stopped' : 'Song rule on'}
            </button>
          )}
          {/* Editor-switch offer stop (admin/staff): shown while the offer window is open or it is stopped. */}
          {(user.role === 'admin' || user.role === 'staff') && (project.editor_switch?.window_open || project.editor_switch?.stopped) && (
            <button onClick={toggleEditorSwitch} disabled={esSaving} data-testid="editor-switch-toggle"
              title={project.editor_switch?.stopped
                ? `The sooner-editor offer is stopped on this project${project.editor_switch_disabled_by ? ` (by ${project.editor_switch_disabled_by})` : ''}. Click to allow it again.`
                : 'The client may be offered a switch to an editor who could start sooner for the next 48 hours. Click to stop it for this project.'}
              className={`tap-auto flex items-center gap-1 text-[9px] font-sans font-semibold uppercase tracking-wider border rounded-full px-2 py-0.5 transition-colors disabled:opacity-50 ${
                project.editor_switch?.stopped ? 'text-ug-status-red border-ug-status-red/40 hover:border-ug-status-red' : 'text-slate-500 hover:text-ug-gold border-ug-border hover:border-gold-leaf/30'}`}>
              <User className="w-3 h-3" />
              {project.editor_switch?.stopped ? 'Switch offer stopped' : 'Switch offer on'}
            </button>
          )}
          {user.role === 'editor' && project.editor_due_date && (project.editor_status_id ?? 0) < 2 && (
            <span className="flex items-center gap-1 ml-auto" data-testid={`drawer-due-countdown-${project.id}`}>
              <Clock className="w-3 h-3 text-slate-500" />
              <DeadlineCountdown dueDate={project.editor_due_date} />
            </span>
          )}
          {project.event_date && (
            <span className={`font-sans text-[10px] font-semibold text-slate-500 flex items-center gap-1 ${user.role === 'editor' && project.editor_due_date && (project.editor_status_id ?? 0) < 2 ? '' : 'ml-auto'}`}>
              <Calendar className="w-3 h-3" />{project.event_date}
            </span>
          )}
        </div>
        {/* While auto-prep holds the order: an editor's and a client's only word of it (R2, lib/autoprep.js). */}
        <AutoPrepNotice project={project} role={user.role} />

        {/* Client: contextual guidance based on current status.
            SUPPRESSED while a file request is open. Otherwise this sits inches
            below the at-risk panel telling the client work "will begin shortly"
            while the panel says delivery is at risk pending their files — two
            pieces of guidance competing for the same question. The panel above
            already says what is happening and what to do, so this one steps
            aside rather than being softened into vagueness. Derived from the
            request's own status (awaiting_client_files), so it comes back by
            itself the moment they respond — nothing to remember to reset. */}
        {/* Same sentence as the tooltip on the status pill above — ONE source
            (lib/clientStatusHelp.js), because these are now read in two places
            and a second inline copy is how they drift. The culling variant is
            chosen inside the helper: the standard copy points at the Review tab,
            which a culling job does not have. */}
        {user.role === 'client' && !project.awaiting_client_files && (() => {
          // The keep ask is left off the sentence while the standing line below says it (lib/clientStatusHelp `tail`).
          const guidance = clientStatusHelp(project.client_status_id, isCullingClient, filesRemoved(project),
            project.keep_footage_link, { tail: !keepsLink(project) });
          return guidance ? (
            <p className="font-sans text-[10px] text-zinc-500 leading-relaxed mt-2 px-0.5" data-testid="status-guidance">{guidance}</p>
          ) : null;
        })()}
        {/* The standing line (lib/keepLink.js, Ryan 2026-10-26): an open order whose footage is the client's own link,
            before its first draft. The server decides (keep_footage_link); a hard drive, a portal upload or Our Dropbox
            copy never shows it. Shown even while a file request is open: it is about the footage they already sent. */}
        {user.role === 'client' && keepsLink(project) && <KeepFootageLink className="mt-2" />}

        {/* Products */}
        {project.products?.length > 0 && (
          <div className="flex gap-1.5 mt-3 flex-wrap items-center">
            {project.products.map((p, i) => (
              <span key={i} className="text-[10px] font-mono bg-white/[0.04] border border-ug-border px-2 py-0.5 rounded text-slate-400">{sanitizeProductLabel(p, user.role)}</span>
            ))}
            {user.role === 'admin' && !project.two_edits && project.project_type !== 'photo' && (project.line_items?.length === 1) && (
              <button onClick={() => setShowChangeProduct(true)} data-testid="change-product-btn"
                title="Change edit type" aria-label="Change edit type"
                className="tap-auto p-1 rounded text-slate-500 hover:text-ug-gold hover:bg-white/[0.04] transition-colors">
                <Pencil className="w-3 h-3" />
              </button>
            )}
            {(user.role === 'client' || user.role === 'admin' || user.role === 'staff') && (
              <button onClick={() => { window.location.href = `/new-order?addon=${project.id}`; }} data-testid="add-edit-btn"
                className="tap-auto flex items-center gap-1 text-[9px] font-sans font-semibold uppercase tracking-wider text-ug-gold/60 hover:text-ug-gold border border-ug-gold/20 hover:border-ug-gold/40 px-2 py-0.5 rounded transition-colors">
                <Plus className="w-3 h-3" /> Add Edit
              </button>
            )}
            {/* Own archive: the caller's list only (server-side per role). Approved
                jobs only, same gate as the board's. Outside the work lock on purpose:
                an editor tidies a job we have locked just the same. */}
            {ownArchive && project.client_status_id >= 8 && (
              <button onClick={toggleOwnArchive} disabled={archiving} data-testid="own-archive-btn"
                title={project.my_archived ? 'Put this back on your list' : 'Move this off your list. Only your list; nobody else sees a change.'}
                className="tap-auto flex items-center gap-1 text-[9px] font-sans font-semibold uppercase tracking-wider text-slate-500 hover:text-ug-gold border border-ug-border hover:border-gold-leaf/30 rounded-full px-2 py-0.5 transition-colors disabled:opacity-50">
                {project.my_archived ? <ArchiveRestore className="w-3 h-3" /> : <Archive className="w-3 h-3" />}
                {project.my_archived ? 'Un-archive' : 'Archive'}
              </button>
            )}
            {user.role === 'client' && project.client_approved && (
              <button onClick={() => { window.location.href = `/new-order?reorder=${project.id}`; }} data-testid="reorder-btn"
                className="flex items-center gap-1 text-[9px] font-sans font-semibold uppercase tracking-wider text-slate-500 hover:text-ug-gold border border-ug-border hover:border-ug-gold/30 px-2 py-0.5 rounded transition-colors">
                Reorder
              </button>
            )}
          </div>
        )}
        {/* P1 shorted-delivery fix: paid deliverable expectation directly under the
            product pills — field only present for editor/admin/staff on photo projects */}
        {project.expected_photos?.total > 0 && (
          <div className="mt-2">
            <ExpectedPhotosChip project={project} testId="expected-photos-chip" />
          </div>
        )}
        {user.role === 'admin' && showChangeProduct && (
          <ChangeProductModal project={project} open={showChangeProduct} onOpenChange={setShowChangeProduct} onChanged={onRefresh} />
        )}
        {/* Frame Stills (backend app/frame_stills.py): a film's, for the team. Not on photo or culling work (no film),
            and not on the Frame Stills order itself (the list row carries its product, not its flag). */}
        {['admin', 'staff', 'billing'].includes(user.role) && project.project_type !== 'photo' && !isCullingProject
          && project.product_name !== 'Frame Stills' && !project.deleted && (
          <FrameStillsPanel project={project} user={user} />
        )}
      </div>

      <div className="gold-line mx-5" />

      {user.role === 'client' && project.editor_switch?.window_open && <EditorSwitchPanel project={project} onRefresh={onRefresh} />}
      {/* Files gone under the retention policy: said once, at the top, before any tab tries
          to play or list them. The same sentence a client and a staffer read; staff also see
          how long the way back stays open. lib/retentionNotice.js owns the words. */}
      {retention && (
        <div data-testid="retention-notice" data-state={retention.state}
          className="mx-5 mt-2 mb-1 px-3 py-2 rounded-md bg-ug-status-red/[0.06] border border-ug-status-red/20 flex items-start gap-2">
          <Lock className="w-3.5 h-3.5 text-ug-status-red/70 shrink-0 mt-0.5" />
          <div className="flex flex-col gap-0.5 min-w-0">
            <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-ug-status-red">
              {retention.state === 'gone' ? 'Files removed' : 'Files removed, can be brought back'}
            </span>
            <span className="font-sans text-[10px] text-slate-500/90 normal-case tracking-normal">{retention.text}</span>
            {retention.state === 'removed' && (user.role === 'admin' || user.role === 'staff') && (
              <span className="font-sans text-[9px] text-slate-500/90">
                {restore ? `Restore is open until ${retention.until}: ${restore.label.replace('Restore files: ', '')}.` : `The restore window closed on ${retention.until}.`}
              </span>
            )}
          </div>
        </div>
      )}
      {isPending && (
        <div className="mx-5 mt-2 mb-1 px-3 py-2 rounded-md bg-gold-leaf/[0.03] border border-gold-leaf/15 flex items-start gap-2">
          <Lock className="w-3.5 h-3.5 text-ug-gold/50 shrink-0 mt-0.5" />
          <div className="flex flex-col gap-0.5 min-w-0">
            <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-ug-gold/60">Pending Assignment — this project is not ready to start yet</span>
            <span className="font-sans text-[9px] font-normal text-slate-500/90 normal-case tracking-normal">You can still message us here while it's being prepped.</span>
          </div>
        </div>
      )}

      {/* Tabs */}
      {/* Client-facing: sits ABOVE the tab strip, not inside a tab, so a client
          arriving from the email lands on it without choosing a tab first. The
          drawer opens on Review for them, and a request buried in Files would be
          a request they never see. */}
      {user.role === 'client' && (
        <div className="px-5 pt-4 shrink-0 empty:hidden">
          <ClientFileRequest project={project} onRefresh={onRefresh} slot="banner" />
        </div>
      )}
      <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col min-h-0 min-w-0 overflow-y-auto">
        <TabsList className="bg-transparent border-b border-ug-border w-full justify-start rounded-none px-3 h-10 shrink-0">
          {[
            ...(project.project_type !== 'photo' && !isCullingProject ? [{ v: 'review', icon: Film, label: 'Review' }] : []),
            ...(isCullingProject ? [{ v: 'delivery', icon: Package, label: 'Delivery' }] : []),
            ...(project.project_type === 'photo' ? [{ v: 'photos', icon: Image, label: 'Review' }] : []),
            { v: 'files', icon: FileText, label: 'Files' },
            { v: 'notes', icon: FileText, label: 'Notes' },
            ...(isVideoCullingJob ? [{ v: 'culling', icon: Scissors, label: 'Culling' }] : []),
            ...((user.role !== 'client') ? [{ v: 'status', icon: Settings, label: 'Status' }] : []),
            { v: 'feedback', icon: Star, label: 'Feedback' },
            ...((user.role === 'admin' || user.role === 'staff' || user.role === 'client') ? [{ v: 'profile', icon: Palette, label: 'Profile' }] : []),
            { v: 'activity', icon: Clock, label: 'Activity' },
          ].map(t => (
            <TabsTrigger key={t.v} value={t.v} data-testid={`tab-${t.v}`}
              // shadow-none cancels the shadcn TabsTrigger default
              // `data-[state=active]:shadow` (ui/tabs.jsx). It's invisible against
              // the dark theme but in light mode it drew as vertical edges either
              // side of the active tab. The active state is already carried by the
              // gold bottom border + text colour.
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-gold-leaf data-[state=active]:bg-transparent data-[state=active]:text-ug-gold data-[state=active]:shadow-none text-slate-500 font-mono uppercase text-[9px] tracking-wider px-2.5 h-10 transition-colors">
              <t.icon className="w-3 h-3 mr-1" />{t.label}
              {/* Wayfinding 2/3: for a client who navigated away from the panel or
                  went to Files first. Derived, clears itself. Uses the same solid
                  gold dot the app already uses for attention, not a new badge. */}
              {t.v === 'files' && user.role === 'client' && project.awaiting_client_files && (
                <span data-testid="tab-files-request-dot"
                      className="ml-1 inline-block w-1.5 h-1.5 rounded-full bg-ug-gold align-middle" />
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        <ScrollArea className="flex-1 min-h-0 drawer-scroll-area">
          {/* Activity Tab */}
          <TabsContent value="activity" className="p-5 m-0 space-y-4">
            <ErrorBoundary fallback={tabErrorFallback}>
            {/* Order Details — admin only (editors, staff, PMs, and clients must NOT see pricing) */}
            {user.role === 'admin' && project.line_items?.length > 0 && (
              <div className="mb-5">
                <h3 className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold/70 mb-3">Order Details</h3>
                <div className="p-3 rounded-xl bg-ug-raised border border-ug-border p-5 space-y-1.5">
                  {project.line_items.map((item, i) => (
                    <div key={i} className="flex justify-between">
                      <span className="text-[11px] text-zinc-100/70">{item.label}</span>
                      <span className="font-sans text-[11px] font-medium text-zinc-100">${(item.amount || 0).toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="border-t border-ug-border-subtle pt-1.5 mt-1.5">
                    <div className="flex justify-between">
                      <span className="font-sans text-[9px] font-semibold text-slate-500 uppercase">Subtotal</span>
                      <span className="font-sans text-[11px] font-medium text-zinc-100">${(project.subtotal || 0).toFixed(2)}</span>
                    </div>
                    {project.discount_type === 'fixed' && project.discount_amount > 0 && (
                      <div className="flex justify-between">
                        <span className="font-sans text-[9px] font-semibold text-oxidized-teal uppercase">Discount (${project.discount_value} off){project.discount_code ? `: ${project.discount_code}` : ''}</span>
                        <span className="font-sans text-[11px] font-medium text-oxidized-teal">-${(project.discount_amount || 0).toFixed(2)}</span>
                      </div>
                    )}
                    {project.discount_percent > 0 && (
                      <div className="flex justify-between">
                        <span className="font-sans text-[9px] font-semibold text-oxidized-teal uppercase">Discount ({project.discount_percent}%){project.discount_code ? ` — ${project.discount_code}` : ''}</span>
                        <span className="font-sans text-[11px] font-medium text-oxidized-teal">-${(project.discount_amount || 0).toFixed(2)}</span>
                      </div>
                    )}
                    {project.offer_path && project.offer_path !== 'none' && (
                      <div className="flex justify-between">
                        <span className="font-sans text-[8px] text-slate-500 uppercase">{project.offer_path === 'discount_code' ? 'Promo Code' : project.offer_path === 'two_versions' ? 'Two Versions Offer' : project.offer_path === 'first_order_50' ? 'First Order 50%' : ''}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="font-sans text-[9px] font-semibold text-ug-gold uppercase font-medium">Total</span>
                      <span className="font-sans text-sm text-ug-gold font-medium">${(project.total || 0).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Referral: who sent this client to us (they used that studio's code at checkout), and what
                became of each reward the referrer earns: $100 on this client's second qualifying order,
                $50 on the third (app/referrals.py, operator 2026-09-22). The server sends it to admin
                and staff only, and only when somebody referred them. */}
            {project.referral && (
              <div className="mb-5" data-testid="drawer-referral">
                <h3 className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold/70 mb-3">Referral</h3>
                <div className="rounded-xl bg-ug-raised border border-ug-border p-4 space-y-1">
                  <p className="text-[11px] text-zinc-100/70">Referred by <span className="text-zinc-100">{project.referral.referrer_name}</span></p>
                  {project.referral.rewards.map(rw => (
                    <p key={rw.tier} data-testid={`drawer-referral-status-${rw.tier}`} className={`text-[11px] ${
                      rw.status === 'issued' ? 'text-oxidized-teal'
                        : ['blocked', 'unconfirmed', 'failed'].includes(rw.status) ? 'text-ug-status-red' : 'text-slate-400'}`}>
                      {rw.status === 'issued' ? `$${rw.amount} reward for their ${rw.ordinal} order sent to the referrer: code ${rw.code}.`
                        : rw.status === 'blocked' ? `No $${rw.amount} reward for their ${rw.ordinal} order: ${rw.reason}`
                        : rw.status === 'unconfirmed' ? `No $${rw.amount} reward yet. ${rw.reason}`
                        : ['failed', 'claimed'].includes(rw.status) ? `Reward not sent yet: $${rw.amount} for their ${rw.ordinal} order${rw.order_at ? `, paid ${formatEastern(rw.order_at, { dateOnly: true })}` : ''}. It is retried every day at 14:00 UTC.`
                        : `$${rw.amount} reward not earned yet. It is sent when this client pays for their ${rw.ordinal} order.`}
                    </p>
                  ))}
                </div>
              </div>
            )}

            {/* The studios THIS client referred, and where each stands: the same list the client sees
                on their Gem Bag, plus each studio's last paid order and a filter for those that
                stopped at one order or at two (components/ReferralList). Draws nothing for a client
                who referred nobody. */}
            {(user.role === 'admin' || user.role === 'staff') && project.client_id && (
              <ReferredStudios clientId={project.client_id} />
            )}

            {/* Versions */}
            {project.versions?.length > 0 && (
              <div className="mb-5">
                <h3 className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold/70 mb-3">Deliverables</h3>
                <div className="space-y-2">
                  {[...project.versions].sort((a, b) => (b.version_number || 0) - (a.version_number || 0)).map((v) => (
                    <div key={v.version_number} className={`p-3 rounded-md border transition-colors ${v.version_number === project.current_version ? 'bg-gold-leaf/[0.04] border-gold-leaf/15' : 'bg-ug-surface/50 border-ug-border-subtle'}`}>
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <span className="font-sans text-sm font-medium text-oxidized-teal">V{v.version_number}</span>
                          <span className="text-[10px] text-slate-500">{v.status_at_creation}</span>
                        </div>
                        <span className="font-sans text-[9px] font-semibold text-slate-600">{formatEastern(v.created_at, { dateOnly: true })}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Status History */}
            <h3 className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold/70 mb-3">Activity Log</h3>
            <div className="space-y-2.5">
              {/* A client's log goes through activityFor: milestones only, the editor never named
                  (lib/clientActivity.js; the server applies the same rule first). */}
              {activityFor(user.role, project.status_history).slice().reverse()
                .map((ev, i) => (
                <div key={i} className="flex gap-3 items-start">
                  <div className={`w-1.5 h-1.5 rounded-full mt-2 shrink-0 ${ev.status_type === 'due_date' ? 'bg-oxidized-teal/60' : ev.status_type === 'payment' ? 'bg-emerald-500/60' : ev.status_type === 'assignment' ? 'bg-indigo-400/60' : ev.status_type === 'upload_status' ? 'bg-amber-400/60' : ev.status_type === 'footage_download' ? 'bg-amber-400/60' : ev.status_type === 'footage_received' ? 'bg-oxidized-teal/60' : (ev.status_type === 'system' && ev.label === 'Revisions Requested') ? 'bg-sky-400/60' : 'bg-gold-leaf/40'}`} />
                  <div>
                    <p className="text-[12px] text-zinc-100/80">
                      {ev.status_type === 'payment' ? (
                        <>
                          <span className="text-emerald-500/80 font-sans text-[9px] font-semibold uppercase tracking-wider">Payment</span>
                          {' '}{ev.label || 'Payment status changed'}
                          {ev.actor_name && <span className="text-slate-600 text-[9px] ml-1">by {ev.actor_name}</span>}
                          {ev.note && <span className="text-slate-500 text-[9px] block mt-0.5 italic">"{ev.note}"</span>}
                        </>
                      ) : ev.status_type === 'due_date' ? (
                        <>
                          <span className="text-oxidized-teal/80 font-sans text-[9px] font-semibold uppercase tracking-wider">Due Date</span>
                          {' '}{ev.new_due_date ? (ev.old_due_date ? `${ev.old_due_date} → ${ev.new_due_date}` : `Set to ${ev.new_due_date}`) : `Cleared${ev.old_due_date ? ` (was ${ev.old_due_date})` : ''}`}
                          {ev.source && <span className="text-slate-600 text-[9px] ml-1">({ev.source === 'manual_override' ? `Manual — ${ev.actor_name || 'admin'}` : ev.source === 'manual_clear' ? `Cleared — ${ev.actor_name || 'admin'}` : ev.source === 'auto_calculated' ? 'Auto-calculated' : ev.source === 'late_cascade' ? 'Late cascade' : ev.source === 'manual_cascade' ? 'Queue re-stacked' : ev.source})</span>}
                        </>
                      ) : ev.status_type === 'assignment' ? (
                        <>
                          <span data-testid="activity-assignment-tag" className="text-indigo-400/80 font-sans text-[9px] font-semibold uppercase tracking-wider">Assignment</span>
                          {' '}{ev.label || 'Editor assignment changed'}
                          {ev.actor_name && <span className="text-slate-600 text-[9px] ml-1">by {ev.actor_name}</span>}
                        </>
                      ) : ev.status_type === 'upload_status' ? (
                        <>
                          <span data-testid="activity-upload-status-tag" className="text-amber-400/80 font-sans text-[9px] font-semibold uppercase tracking-wider">Footage</span>
                          {' '}{ev.label || 'Upload status updated'}
                          {ev.actor_name && <span className="text-slate-600 text-[9px] ml-1">by {ev.actor_name}</span>}
                        </>
                      ) : ev.status_type === 'footage_received' ? (
                        <>
                          <span data-testid="activity-footage-received-tag" className="text-oxidized-teal/80 font-sans text-[9px] font-semibold uppercase tracking-wider">Footage</span>
                          {' '}{ev.label || (ev.received ? 'Footage marked received (manual)' : 'Footage receipt cleared')}
                          {ev.actor_name && <span className="text-slate-600 text-[9px] ml-1">by {ev.actor_name}</span>}
                        </>
                      ) : ev.status_type === 'footage_download' ? (
                        <>
                          <span data-testid="activity-footage-download-tag" className="text-amber-400/80 font-sans text-[9px] font-semibold uppercase tracking-wider">Footage</span>
                          {' '}{ev.label || 'Footage download started'}
                          {ev.actor_name && <span className="text-slate-600 text-[9px] ml-1">by {ev.actor_name}</span>}
                        </>
                      ) : (
                        <>
                          {ev.status_type === 'system' && ev.label === 'Revisions Requested' ? (
                            <span data-testid="activity-client-tag" className="text-sky-400/80 font-sans text-[9px] font-semibold uppercase tracking-wider">Client</span>
                          ) : (
                            <span className="text-ug-gold/60 font-sans text-[9px] font-semibold uppercase tracking-wider">{ev.status_type}</span>
                          )}
                          {' '}{ev.status_type === 'system' ? ev.label : ev.status_type === 'admin' ? ADMIN_STATUSES[ev.new_status_id] : ev.new_status_id != null ? EDITOR_STATUSES[ev.new_status_id] : ev.label || ''}
                          {ev.actor_name && <span className="text-slate-600 text-[9px] ml-1">by {ev.actor_name}</span>}
                          {/* A move the machine made says why, from the history alone (auto-advance). */}
                          {ev.reason && <span data-testid="activity-reason" className="text-slate-500 text-[9px] block mt-0.5 italic">{ev.reason}</span>}
                        </>
                      )}
                    </p>
                    <p className="font-sans text-[9px] font-semibold text-slate-600">{formatEastern(ev.timestamp)}</p>
                  </div>
                </div>
              ))}
              {(!project.status_history?.length) && <p className="text-[11px] text-slate-600">No activity yet</p>}
            </div>

            {/* Project ID — for the footage pull tool's run command. Internal roles only,
                never clients: they have no reason to run the tool and it is not their concern. */}
            {user.role !== 'client' && (
              <div data-testid="drawer-project-id" className="mt-6 pt-4 border-t border-ug-border-subtle">
                <h3 className="font-sans text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Project ID</h3>
                <div className="flex items-center gap-2">
                  <code data-testid="drawer-project-id-value" className="flex-1 font-mono text-[11px] text-slate-400 bg-white/[0.03] border border-ug-border rounded px-2 py-1.5 break-all select-all">{project.id}</code>
                  <button
                    type="button"
                    data-testid="drawer-project-id-copy"
                    onClick={() => {
                      const cmd = `py footage_pull.py ${project.id} "<destination folder>"`;
                      (navigator.clipboard?.writeText(cmd) || Promise.reject())
                        .then(() => { setIdCmdCopied(true); setTimeout(() => setIdCmdCopied(false), 2000); })
                        .catch(() => toast.error('Copy failed — select the ID and copy it manually'));
                    }}
                    title='Copy the footage-pull command'
                    className="tap-auto shrink-0 inline-flex items-center gap-1 text-[10px] font-medium text-slate-400 hover:text-zinc-100 border border-ug-border rounded px-2 py-1.5 transition-colors">
                    {idCmdCopied ? <><Check className="w-3 h-3 text-emerald-400" /> Copied</> : <><Copy className="w-3 h-3" /> Copy command</>}
                  </button>
                </div>
                <p className="text-[9px] text-slate-600 mt-1.5">Copies the footage pull command; replace <span className="font-mono">&quot;&lt;destination folder&gt;&quot;</span> with where the footage should go.</p>
              </div>
            )}
            </ErrorBoundary>
          </TabsContent>

          {/* Review Tab — video player with timestamped comments.
              NOT rendered for culling: this TabsContent is forceMount, so without
              the guard VideoReview would still mount (hidden) on a culling job and
              run its stream polling / footage-report fetch for a file-only delivery. */}
          {!isCullingProject && (
          <TabsContent value="review" className="m-0" forceMount style={{ display: tab === 'review' ? undefined : 'none' }}>
            <ErrorBoundary fallback={tabErrorFallback}>
            <VideoReview project={project} onRefresh={onRefresh} readOnly={isLocked} onUploading={handleUploading} onFullscreen={() => {
              // Pause the drawer video and capture its current time
              const v = document.querySelector('[data-testid="review-tab"] video');
              if (v) { v.pause(); setFullscreenInitialTime(v.currentTime || 0); }
              setFullscreen('review');
            }} />
            </ErrorBoundary>
          </TabsContent>
          )}

          {/* Delivery Tab — culling-only clients: download panel, no player */}
          <TabsContent value="delivery" className="m-0">
            <ErrorBoundary fallback={tabErrorFallback}>
              <CullingDelivery project={project} onRefresh={onRefresh} />
            </ErrorBoundary>
          </TabsContent>

          {/* Photos Tab — photo review gallery */}
          <TabsContent value="photos" className="m-0 overflow-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
            <PhotoReview project={project} onRefresh={onRefresh} onFullscreen={() => setFullscreen('photos')} />
          </TabsContent>

          {/* Files Tab — UNIFIED across all portals */}
          <TabsContent value="files" className="p-5 m-0">
            <ErrorBoundary fallback={tabErrorFallback}>
            <div className="flex flex-col gap-5">
              {/* Footage block — shared with Redemption Cut Files tab.
                  Canonical render lives in FootageLinks.js. */}
              {/* Wayfinding 3/3: once a client is IN the Files tab, point them back
                  up to the panel above the tab strip. Derived, clears itself. */}
              {user.role === 'client' && project.awaiting_client_files && (
                <div className="rounded-xl bg-ug-raised border border-ug-gold/40 px-4 py-3"
                     data-testid="files-tab-request-pointer">
                  <span className="font-sans text-[11px] text-zinc-300">
                    <span className="font-semibold text-ug-gold">We&rsquo;ve asked for something.</span>{' '}
                    See the note at the top of this panel to reply.
                  </span>
                </div>
              )}
              {/* The Download button: opens Footage Pull with this project loaded. Admin,
                  staff and the assigned editor (the only editor who can open the project).
                  Not clients, who have no reason to pull their own footage, and not billing. */}
              {['admin', 'staff', 'editor'].includes(user.role) && <FootagePullButton project={project} role={user.role} />}
              {['admin', 'staff'].includes(user.role) && <AutoPrepCard project={project} user={user} />}
              <FootageLinks project={project} role={user.role} onRefresh={onRefresh} />
              {/* Client, STATE B only (already answered "I don't have these").
                  Sits under the footage for the same reason the editor's and
                  admin's panels do — it is about the files, and it is next to the
                  upload area the "Found them after all?" field refers to. The
                  UNANSWERED state deliberately does NOT render here; it stays
                  above the tab strip where a client from the email lands on it. */}
              {user.role === 'client' && (
                <ClientFileRequest project={project} onRefresh={onRefresh} slot="files" />
              )}
              {/* Editor-only, and directly under the footage: the moment an editor
                  SEES the footage is the moment they notice a card is missing, so
                  the ask sits where the discovery happens, not behind a menu. */}
              {user.role === 'editor' && project.client_id && (
                <FileRequestPanel project={project} onRefresh={onRefresh} />
              )}
              {/* Admin/staff sit in the same place in the Files tab as the editor's
                  panel — directly under the footage — because they are looking at
                  the same question from the other side. Before this, this tab
                  rendered admin nothing at all about file requests while the
                  feature's escalations claimed to reach them. */}
              {(user.role === 'admin' || user.role === 'staff') && (
                <AdminFileRequest project={project} onRefresh={onRefresh} />
              )}
              {/* Brand Assets */}
              {project.project_type !== 'photo' && (
                <div className="rounded-xl bg-ug-raised border border-ug-border p-5">
                  <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-ug-gold/60 block mb-2">Brand Assets</span>
                  {clientProfile?.video?.assets_link ? <a href={clientProfile.video.assets_link.startsWith('http') ? clientProfile.video.assets_link : `https://${clientProfile.video.assets_link}`} target="_blank" rel="noopener noreferrer" className="text-[11px] text-oxidized-teal hover:text-oxidized-teal/80 underline underline-offset-2 break-all">{clientProfile.video.assets_link}</a> : <p className="text-[10px] text-slate-600 italic">No link provided</p>}
                </div>
              )}
              {/* Music */}
              {project.project_type !== 'photo' && (
                <div className="rounded-xl bg-ug-raised border border-ug-border p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-ug-gold/60">Music</span>
                    <div className={`ready-indicator ${project.music_ready ? 'ready' : 'not-ready'}`}>{project.music_ready ? <Check className="w-3 h-3" strokeWidth={2.5} /> : <X className="w-3 h-3" strokeWidth={2} />}</div>
                  </div>
                  <p className="text-[11px] text-zinc-100/70 mb-2">{project.music_choice === 'uncut_gems_chooses' ? 'Uncut Gems selects music' : project.music_choice === 'na_culling' ? 'N/A — Culling only' : 'Client provides music'}</p>
                  {project.music_notes && <div className="p-2.5 rounded bg-ug-surface/50 border border-ug-border-subtle mb-2"><span className="font-sans text-[8px] font-medium uppercase tracking-wider text-slate-500 block mb-1">Music Notes</span><p className="text-[11px] text-zinc-100/70 whitespace-pre-wrap">{project.music_notes}</p></div>}
                  {files.filter(fl => fl.file_type === 'music').map(fl => {
                    const downloadUrl = fl.file_id && !filesRemoved(project) ? `${process.env.REACT_APP_BACKEND_URL}/api/storage/serve/${fl.file_id}` : '';
                    return (
                      <div key={fl.id} className="flex items-center gap-2 p-2 rounded bg-ug-surface/50 border border-ug-border-subtle mb-1">
                        <FileText className="w-3 h-3 text-oxidized-teal" />
                        {downloadUrl ? (
                          <a href={downloadUrl} className="text-[11px] text-oxidized-teal hover:text-oxidized-teal/80 underline underline-offset-2 flex-1 truncate">{fl.filename}</a>
                        ) : fl.url && !filesRemoved(project) ? (
                          <button onClick={async () => {
                            try {
                              const r = await api.post('/storage/presign-download', { key: fl.url });
                              window.location.href = r.data.download_url;
                            } catch (err) {
                              if (err.response?.status === 410) {
                                toast.error(err.response.data?.message || 'This file is no longer available in storage. Please contact support.');
                              } else {
                                toast.error('Download failed');
                              }
                            }
                          }} className="text-[11px] text-oxidized-teal hover:text-oxidized-teal/80 underline underline-offset-2 flex-1 truncate text-left">{fl.filename}</button>
                        ) : (
                          <span className="text-[11px] text-zinc-100/80 flex-1 truncate">{fl.filename}</span>
                        )}
                      </div>
                    );
                  })}
                  {(user.role === 'client' || user.role === 'admin' || user.role === 'staff') && project.music_choice !== 'uncut_gems_chooses' && project.music_choice !== 'na_culling' && (
                    <FileUploadZone scope="project_asset" projectId={project.id} multiple accept="audio/*,.mp3,.wav,.aiff,.flac,.m4a" label="Upload Music" hint="Drop song files here (.mp3, .wav, .aiff)" onUploadComplete={async (uploaded) => { for (const fl of uploaded) { await api.post(`/projects/${project.id}/files`, { filename: fl.filename, file_type: 'music', url: fl.key }); } onRefresh(); }} />
                  )}
                </div>
              )}
              {/* Lightroom Preset(s) (photo) */}
              {project.project_type === 'photo' && (() => {
                const ph = clientProfile?.photo || {};
                const presets = (Array.isArray(ph.presets) && ph.presets.length)
                  ? ph.presets
                  : ((ph.preset_filename || ph.preset_file_id) ? [{ file_id: ph.preset_file_id, filename: ph.preset_filename }] : []);
                if (!presets.length) return null;
                return (
                  <div className="rounded-xl bg-ug-raised border border-ug-border p-5">
                    <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-ug-gold/60 block mb-2">{presets.length > 1 ? 'Lightroom Presets' : 'Lightroom Preset'}</span>
                    <div className="flex flex-col gap-1.5">
                      {presets.map((pr, i) => (
                        pr.file_id ? (
                          <a key={pr.file_id || i} href={`${process.env.REACT_APP_BACKEND_URL}/api/storage/serve/${pr.file_id}`} className="text-[12px] text-oxidized-teal hover:text-oxidized-teal/80 underline underline-offset-2" data-testid={i === 0 ? 'files-tab-preset-download' : `files-tab-preset-download-${i}`}>{pr.filename}</a>
                        ) : (
                          <p key={i} className="text-[12px] text-zinc-100/80">{pr.filename}</p>
                        )
                      ))}
                    </div>
                  </div>
                );
              })()}

            </div>
            </ErrorBoundary>
          </TabsContent>




          {/* Notes Tab — editable until assigned, then add-note system */}
          <TabsContent value="notes" className="p-5 m-0 space-y-4">
            {/* Event date surfaced at the top of Notes (moved off the board).
               Both testid-carrying elements MUST include .tap-auto to escape the
               global `[data-testid] { min-height: 44px }` inflation, else the
               value span balloons to 44px and floats above the label baseline
               (occurrence #7 of the same fingerprint). */}
            <div className="tap-auto flex items-center gap-2 pb-3 border-b border-ug-border-subtle leading-none" data-testid="notes-event-date">
              <Calendar className="w-3.5 h-3.5 text-ug-gold/60 tap-auto shrink-0" strokeWidth={1.5} />
              <span className="font-sans text-[10px] font-semibold uppercase tracking-wider text-slate-500 leading-none">Event Date:</span>
              <span className="tap-auto font-sans text-[13px] font-medium text-zinc-100/90 leading-none" data-testid="notes-event-date-value">
                {project.event_date
                  ? new Date(project.event_date + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                  : 'Not set'}
              </span>
            </div>
            {/* THE NOTES SECTION (Ryan, 2026-09-25): one section at the top of the project for the assigned editor
               and the team. This project (what the client wrote for this order), then the AI style notes carrying
               the whole Style Profile (or the Style Profile itself when there are none), then what the client wrote
               in their Style Profile, closed. It replaces every block below that showed the client's notes or
               profile, for editors and the team alike; the team edits the order notes and adds notes in it, and
               opens the full raw profile (the Profile tab) from it. The client and billing keep the blocks below. */}
            {notesInSection && (
              <NotesSection project={project} files={files} fallbackProfile={clientProfile}
                renderProfile={(p) => <StyleProfileView profile={p} showOnly={project.project_type} project={project}
                  isEditor={user.role === 'editor'} excludeAssets={user.role === 'editor'} showMustHaves />}
                onSaveNotes={user.role === 'editor' ? undefined : (v) => handleUpdateProject('notes', v)}
                onOpenProfile={user.role === 'editor' ? undefined : () => setTab('profile')} />
            )}
            {/* FROM THE STYLE PROFILE — surfaced here on purpose (2026-08-25).
               A film came back chronological when the profile said
               non-chronological and the notes said "don't end on the exit",
               which became a billing dispute. The information existed; it was
               one tab away and the editor works from Notes. Storytelling is the
               field that decides the shape of the edit, so it is repeated here
               rather than linked to. */}
            {(() => {
              if (notesInSection) return null;   // the Notes section above carries it
              const sp = clientProfile?.[project.project_type === 'photo' ? 'photo' : 'video'] || {};
              const story = sp.story_telling;
              const spNotes = sp.additional_notes;
              // a doc edit carries the client's doc edit notes too (they feed doc edits only)
              const spDoc = project.project_type !== 'photo' && resolveProjectCategory(project) === 'doc_edit' ? sp.doc_edit_notes : '';
              if (!story && !spNotes && !sp.music_source && !spDoc) return null;
              return (
                <div className="!mt-2 p-3 rounded-xl bg-oxidized-teal/[0.05] border border-oxidized-teal/25"
                  data-testid="notes-style-profile-summary">
                  <div className="flex items-center gap-1.5 mb-2">
                    <User className="w-3 h-3 text-oxidized-teal tap-auto shrink-0" strokeWidth={1.5} />
                    <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-oxidized-teal">
                      From the client's style profile
                    </span>
                  </div>
                  {story && (
                    <div className="flex items-baseline gap-2 mb-1.5" data-testid="notes-story-telling">
                      <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-slate-500 shrink-0">
                        Storytelling
                      </span>
                      <span className="tap-auto font-sans text-[13px] font-semibold text-zinc-100 leading-none">
                        {String(story).replace(/_/g, ' ')}
                      </span>
                    </div>
                  )}
                  {sp.music_source && (
                    <div className="flex items-baseline gap-2 mb-1.5">
                      <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-slate-500 shrink-0">
                        Music
                      </span>
                      <span className="text-[12px] text-zinc-100/85">{String(sp.music_source).replace(/_/g, ' ')}</span>
                    </div>
                  )}
                  {spNotes && (
                    <p className="text-[12px] text-zinc-100/80 whitespace-pre-wrap leading-relaxed mt-1.5 pt-1.5 border-t border-oxidized-teal/15">
                      {spNotes}
                    </p>
                  )}
                  {spDoc && (
                    <div className="mt-1.5 pt-1.5 border-t border-oxidized-teal/15" data-testid="notes-doc-edit-notes">
                      <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-slate-500 block mb-1">{DOC_EDIT_NOTES_LABEL}</span>
                      <p className="text-[12px] text-zinc-100/80 whitespace-pre-wrap leading-relaxed">{spDoc}</p>
                    </div>
                  )}
                  <button type="button" onClick={() => setTab('profile')}
                    data-testid="notes-open-style-profile"
                    className="tap-auto mt-2 text-[10px] text-oxidized-teal hover:text-oxidized-teal/80 underline underline-offset-2">
                    This is a summary -- open the Profile tab for the full style profile
                  </button>
                </div>
              );
            })()}

            {/* The Notes section above carries all of this for editors and the team; the client and billing keep it here. */}
            {!notesInSection && (<>
              <div className="flex items-center justify-between">
                <h3 className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold/70">Project Notes</h3>
              </div>
              {/* Client pre-assignment: always editable textarea */}
              {user.role === 'client' && project.client_status_id <= 2 ? (
                <textarea data-testid="client-notes-textarea" defaultValue={project.notes || ''} onBlur={e => handleUpdateProject('notes', e.target.value)}
                  rows={6} className="w-full bg-ug-surface border border-ug-border text-zinc-100 rounded-md p-3 text-sm focus:border-gold-leaf/40 outline-none resize-y placeholder:text-white/40"
                  placeholder="Add editing notes, style preferences, music selections..." />
              ) : (
                <div className="!mt-2 p-3 bg-ug-surface/50 rounded-xl border border-ug-border text-[13px] text-zinc-100/80 whitespace-pre-wrap leading-relaxed">
                  {project.notes || 'No project notes'}
                </div>
              )}
              {user.role === 'client' && project.client_status_id > 2 && (
                <p className="font-sans text-[9px] font-semibold text-slate-600 mt-2 flex items-center gap-1">
                  <Lock className="w-3 h-3 tap-auto" /> Original notes locked after editor assignment
                </p>
              )}

              {/* Additional notes — client can add anytime after assignment */}

              {/* Project language */}
              {project.wedding_language && project.wedding_language !== 'English' && (
                <div className="!mt-2 p-2 rounded bg-gold-leaf/[0.04] border border-gold-leaf/15">
                  <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-ug-gold/60">Language: </span>
                  <span className="text-[12px] text-zinc-100">{project.wedding_language}</span>
                </div>
              )}
              {project.wedding_language === 'English' && (
                <div className="!mt-2">
                  <span className="font-sans text-[9px] font-semibold text-slate-600">Language: English</span>
                </div>
              )}
            </>)}

            {/* AI Story Tool — editor/admin only, only shows if URL is configured */}
            {(user.role === 'editor' || user.role === 'admin' || user.role === 'staff') && aiToolUrl && (
              <div className="mt-4">
                <a href={`${aiToolUrl}?project_id=${project.id}`} target="_blank" rel="noopener noreferrer" data-testid="ai-story-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-oxidized-teal/10 border border-oxidized-teal/25 hover:bg-oxidized-teal/20 transition-colors">
                  <Wand2 className="w-4 h-4 text-oxidized-teal" />
                  <span className="font-sans text-[10px] font-semibold uppercase tracking-wider text-oxidized-teal">AI Story Tool</span>
                </a>
              </div>
            )}
            {project.client_status_id > 2 && !notesInSection && (
              <div className="mt-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold/70">Additional Notes</h3>
                </div>
                {/* Existing additional notes */}
                {(project.additional_notes || []).map((note, i) => (
                  <div key={note.id || i} className="mb-2 p-3 bg-ug-surface/50 rounded-xl border border-ug-border">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-sans text-[8px] font-medium uppercase tracking-wider text-ug-gold/50">{note.author_name}</span>
                      <span className="font-sans text-[8px] font-medium text-slate-600">{formatEastern(note.created_at)}</span>
                    </div>
                    <p className="text-[12px] text-zinc-100/70 whitespace-pre-wrap leading-relaxed">{note.body}</p>
                  </div>
                ))}
                {/* Add note form — client only */}
                {user.role === 'client' && (
                  <div className="mt-2" data-testid="add-note-section">
                    <textarea id="additional-note-input" rows={3} placeholder="Add a note for your editor..."
                      className="w-full bg-ug-surface border border-ug-border text-zinc-100 rounded-md p-3 text-sm focus:border-gold-leaf/40 outline-none resize-y placeholder:text-white/40 mb-2" />
                    <Button data-testid="add-note-btn" onClick={async () => {
                      const el = document.getElementById('additional-note-input');
                      const body = el?.value?.trim();
                      if (!body) return;
                      try {
                        await api.post(`/projects/${project.id}/additional-notes`, { body });
                        el.value = '';
                        toast.success('Note added — your editor will be notified');
                        onRefresh();
                      } catch { toast.error('Failed'); }
                    }} className="bg-gold-leaf text-obsidian hover:bg-gold-leaf/90 font-mono uppercase tracking-wider text-[10px] h-9">
                      <Plus className="w-3.5 h-3.5 mr-1" /> Add Note
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Song choices from the editor — renders nothing until one is sent.
                Deliberately OUTSIDE the additional-notes block above: that one is
                gated on client_status_id > 2, and song choices can legitimately be
                sent before the project reaches that status. Nesting it there hid the
                panel from the client entirely on early-status projects. */}
            {user.role === 'client' && project.project_type !== 'photo' && (
              <SongApprovalClient project={project} onRefresh={onRefresh} />
            )}

            {/* Song approval — editor proposes, client answers. Video only; culling-only
                jobs have no edit to score. The client's own music choice and notes are
                in the Notes section at the top of this tab. */}
            {(user.role === 'editor' || user.role === 'admin' || user.role === 'staff')
              && project.project_type !== 'photo' && !isVideoCullingJob && (
              <SongApprovalEditor project={project} onRefresh={onRefresh} />
            )}

            {/* Style profile notes: a summary for billing (editors and the team read the Notes section above) */}
            {user.role !== 'client' && !notesInSection && clientProfile && clientProfile.profile_type && (() => {
              const isPhoto = (project.products || []).some(p => p.toLowerCase().includes('photo') || p.toLowerCase().includes('cull'));
              const isVideo = !isPhoto || (project.order_type === 'video' || project.order_type === 'both');
              const v = clientProfile.video || {};
              const ph = clientProfile.photo || {};
              const docNotes = isVideo && resolveProjectCategory(project) === 'doc_edit' ? v.doc_edit_notes : '';
              const hasVideoNotes = isVideo && (v.additional_notes || v.music_notes || v.lut_notes || docNotes);
              const hasPhotoNotes = isPhoto && (ph.editor_notes || ph.culling_notes);
              if (!hasVideoNotes && !hasPhotoNotes) return null;
              return (
                <div className="mt-1">
                  <div className="gold-line mb-4" />
                  <h3 className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold/70 mb-3">Client Style Profile Notes</h3>
                  <p className="text-[9px] text-slate-600 mb-3">These apply to all projects from this client — pulled from their style profile.</p>

                  {isVideo && v.additional_notes && (
                    <div className="mb-3">
                      <span className="font-sans text-[8px] font-medium uppercase tracking-wider text-oxidized-teal/60 block mb-1">General Video Notes</span>
                      <div className="p-3 bg-ug-surface/50 rounded-xl border border-ug-border text-[12px] text-zinc-100/70 whitespace-pre-wrap leading-relaxed break-all">{v.additional_notes}</div>
                    </div>
                  )}
                  {docNotes && (
                    <div className="mb-3">
                      <span className="font-sans text-[8px] font-medium uppercase tracking-wider text-oxidized-teal/60 block mb-1">{DOC_EDIT_NOTES_LABEL}</span>
                      <div className="p-3 bg-ug-surface/50 rounded-xl border border-ug-border text-[12px] text-zinc-100/70 whitespace-pre-wrap leading-relaxed break-all">{docNotes}</div>
                    </div>
                  )}
                  {isVideo && v.music_notes && (
                    <div className="mb-3">
                      <span className="font-sans text-[8px] font-medium uppercase tracking-wider text-oxidized-teal/60 block mb-1">Music Preferences</span>
                      <div className="p-3 bg-ug-surface/50 rounded-xl border border-ug-border text-[12px] text-zinc-100/70 whitespace-pre-wrap leading-relaxed">{v.music_notes}</div>
                    </div>
                  )}
                  {isVideo && v.lut_notes && (
                    <div className="mb-3">
                      <span className="font-sans text-[8px] font-medium uppercase tracking-wider text-oxidized-teal/60 block mb-1">LUT / Color Notes</span>
                      <div className="p-3 bg-ug-surface/50 rounded-xl border border-ug-border text-[12px] text-zinc-100/70 whitespace-pre-wrap leading-relaxed">{v.lut_notes}</div>
                    </div>
                  )}
                  {isVideo && (v.font_name || v.assets_link) && (
                    <div className="mb-3 flex gap-4 flex-wrap">
                      {v.font_name && <div><span className="font-sans text-[8px] font-medium uppercase tracking-wider text-oxidized-teal/60 block mb-1">Font</span><span className="text-[12px] text-zinc-100/70">{v.font_name}{v.font_size ? ` / ${v.font_size}px` : ''}{v.font_spacing ? ` / spacing ${v.font_spacing}` : ''}</span></div>}
                      {v.assets_link && <div><span className="font-sans text-[8px] font-medium uppercase tracking-wider text-oxidized-teal/60 block mb-1">Assets Link</span><a href={v.assets_link} target="_blank" rel="noopener noreferrer" className="text-[12px] text-oxidized-teal underline underline-offset-2">{v.assets_link.length > 40 ? v.assets_link.slice(0, 40) + '...' : v.assets_link}</a></div>}
                    </div>
                  )}
                  {isPhoto && ph.editor_notes && (
                    <div className="mb-3">
                      <span className="font-sans text-[8px] font-medium uppercase tracking-wider text-oxidized-teal/60 block mb-1">Photo Editor Notes</span>
                      <div className="p-3 bg-ug-surface/50 rounded-xl border border-ug-border text-[12px] text-zinc-100/70 whitespace-pre-wrap leading-relaxed">{ph.editor_notes}</div>
                    </div>
                  )}
                  {isPhoto && ph.culling_notes && (
                    <div className="mb-3">
                      <span className="font-sans text-[8px] font-medium uppercase tracking-wider text-oxidized-teal/60 block mb-1">Culling Instructions</span>
                      <div className="p-3 bg-ug-surface/50 rounded-xl border border-ug-border text-[12px] text-zinc-100/70 whitespace-pre-wrap leading-relaxed">{ph.culling_notes}</div>
                    </div>
                  )}
                  {isPhoto && (ph.white_balance || ph.exposure_contrast || ph.skin_tone_bias) && (
                    <div className="mb-3">
                      <span className="font-sans text-[8px] font-medium uppercase tracking-wider text-oxidized-teal/60 block mb-1">Look Preferences</span>
                      <div className="p-3 bg-ug-surface/50 rounded-xl border border-ug-border text-[12px] text-zinc-100/70 flex gap-4 flex-wrap">
                        {ph.white_balance && <span>WB: {ph.white_balance}</span>}
                        {ph.exposure_contrast && <span>Exposure: {ph.exposure_contrast}</span>}
                        {ph.skin_tone_bias && <span>Skin: {ph.skin_tone_bias}</span>}
                        {ph.bw_usage && <span>B&W: {ph.bw_usage}</span>}
                        {ph.crop_straighten && <span>Crop: {ph.crop_straighten}</span>}
                        {ph.avoid_magenta && <span className="text-oxidized-teal/70">&#10003; Avoid magenta</span>}
                        {ph.avoid_green && <span className="text-oxidized-teal/70">&#10003; Avoid green</span>}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </TabsContent>

          {/* Culling Tab — editor-only, video culling jobs only */}
          {isVideoCullingJob && (
            <TabsContent value="culling" className="m-0">
              <CullingInstructions />
            </TabsContent>
          )}

          {/* Status Tab */}
          <TabsContent value="status" className="p-5 m-0 space-y-4">
            {(user.role === 'admin' || user.role === 'staff' || user.role === 'billing') && (
              <EditorPayControl project={project} onRefresh={onRefresh} />
            )}
            <div className="grid grid-cols-2 gap-3">
              <div data-testid="drawer-section-client-status">
                <h3 className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold/70 mb-2">Client Status</h3>
                <div className="rounded-md flex items-center justify-center text-[9px] font-mono uppercase tracking-wider leading-none text-white" style={{ backgroundColor: STATUS_COLORS[project.client_status_id] || '#94A3B8', height: '36px', minHeight: '36px', maxHeight: '36px' }}>
                  {CLIENT_STATUSES[project.client_status_id]}
                </div>
              </div>
              {project.editor_id && (user.role === 'admin' || user.role === 'staff' || user.role === 'billing') ? (
                <div data-testid="drawer-section-editor-status">
                  <h3 className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold/70 mb-2">Editor Status</h3>
                  {/* Filled NEUTRAL (see index.css), not with a status hue.
                      Editor track ids 1-5 are NOT admin track ids 1-5:
                      STATUS_COLORS[editor_status_id] paints the wrong status —
                      editor 1 "Editing In Progress" would render #5C6478, which
                      means "Project Requested" on every other surface. */}
                  <div className="rounded-md flex items-center justify-center text-[9px] font-mono uppercase tracking-wider leading-none text-slate-300 bg-white/[0.08]" style={{ height: '36px', minHeight: '36px', maxHeight: '36px' }} data-testid="editor-status-badge">
                    {EDITOR_STATUSES[project.editor_status_id] || 'Pending Assignment'}
                  </div>
                </div>
              ) : <div />}
            </div>

            {/* Feedback was collected during approval — no separate prompt needed */}

            {(user.role === 'admin' || user.role === 'staff') && (
              <div data-testid="drawer-section-admin-status">
                <h3 className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold/70 mb-2">Admin Status</h3>
                <Select value={String(project.admin_status_id)} onValueChange={handleAdminStatus}>
                  <SelectTrigger data-testid="admin-status-select" className="w-full bg-ug-surface border-ug-border text-zinc-100 h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-ug-raised border-ug-border-strong">
                    {Object.entries(ADMIN_STATUSES).map(([id, label]) => (
                      <SelectItem key={id} value={id} className="text-zinc-100">{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {user.role === 'billing' && (
              <div>
                <h3 className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold/70 mb-2">Admin Status</h3>
                <div className="h-[36px] rounded-md flex items-center justify-center text-[9px] font-mono uppercase tracking-wider leading-none text-white bg-white/[0.08]">
                  {ADMIN_STATUSES[project.admin_status_id]}
                </div>
              </div>
            )}

            {user.role === 'editor' && project.editor_id && (
              <div>
                <h3 className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold/70 mb-2">Editor Status</h3>
                {user.role === 'editor' && (project.editor_status_id === -1 || project.editor_status_id === null || project.editor_status_id === undefined) ? (
                  <div className="p-3 rounded-lg bg-ug-surface/50 border border-ug-border text-center">
                    <p className="font-sans text-[11px] text-zinc-500">Pending Assignment — waiting for admin to start this project</p>
                  </div>
                ) : (
                <Select value={String(project.editor_status_id ?? '')} onValueChange={handleEditorStatus} disabled={uploadsInProgress}>
                  <SelectTrigger data-testid="editor-status-select" className={`w-full bg-ug-surface border-ug-border text-zinc-100 h-9 text-sm ${uploadsInProgress ? 'opacity-50 cursor-not-allowed' : ''}`}>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent className="bg-ug-raised border-ug-border-strong">
                    {Object.entries(EDITOR_STATUSES)
                      .filter(([id]) => user.role === 'editor' ? [0, 1, 2, 3, 4].includes(parseInt(id)) : true)
                      .map(([id, label]) => (
                      <SelectItem key={id} value={id} className="text-zinc-100">{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                )}
              </div>
            )}

            {(user.role === 'admin' || user.role === 'staff') && (
              <div data-testid="drawer-section-assign-editor">
                <h3 className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold/70 mb-2">Assign Editor</h3>
                <EditorSelect
                  value={project.editor_id || ''}
                  editors={editors}
                  placeholder="Select editor"
                  unassignedLabel={project.editor_id ? `Remove ${project.editor_name || 'editor'}` : 'Unassigned'}
                  triggerClassName="h-9 text-sm w-full"
                  testId="assign-editor-select"
                  onChange={(id) => {
                    if (!id) {
                      if (!project.editor_id) return;
                      // server.py unassign_editor: the editor is told, their queue closes up, the job waits for the next one
                      api.put(`/projects/${project.id}/unassign-editor`, {}).then(() => { toast.success('Editor removed. They have been told.'); onRefresh(); }).catch(e => toast.error(e?.response?.data?.detail || 'Failed'));
                    }
                    else { handleAssignEditor(id); }
                  }}
                />
                {/* Quick swap to backup editors */}
                {clientData && (clientData.backup_editor_ids?.length > 0) && (
                  <div className="mt-3">
                    <p className="font-sans text-[8px] font-medium uppercase tracking-wider text-slate-600 mb-1.5">Quick swap to backup</p>
                    <div className="flex gap-1.5">
                      {(clientData.backup_editor_ids || []).filter(Boolean).map(bid => {
                        const be = editors.find(e => e.id === bid);
                        if (!be || be.restricted || be.id === project.editor_id) return null;
                        return (
                          <button key={bid} onClick={() => handleAssignEditor(bid)} data-testid={`swap-editor-${bid}`}
                            className="px-2.5 py-1.5 rounded border border-ug-border hover:border-gold-leaf/30 hover:bg-gold-leaf/[0.04] transition-all">
                            <span className="font-sans text-[9px] font-semibold text-slate-400">{be.name}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* The revision clock: pause / restart (admin, PMs), don't count a round (admin). */}
            {(user.role === 'admin' || user.role === 'staff') && (
              <RevisionClockPanel project={project} user={user} onChanged={onRefresh} />
            )}

            {/* Project actions: Archive (admin, approved only) + Delete (admin or client pre-approval) */}
            {!isLocked && (
              <div className="pt-3 mt-3 border-t border-ug-border-subtle space-y-3">
                {(user.role === 'admin' || user.role === 'staff') && project.client_status_id >= 8 && !project.archived && (
                  <button onClick={async () => { if (!window.confirm('Archive this project? It will move to the Archived tab.')) return; api.put(`/projects/${project.id}/archive`).then(() => { toast.success('Project archived'); onClose(); onRefresh(); }).catch(e => toast.error(e.response?.data?.detail || 'Failed')); }}
                    data-testid="archive-project-btn" className="w-full flex items-center justify-center gap-2 py-2 rounded-xl border border-ug-border text-[10px] font-mono uppercase tracking-wider text-slate-500 hover:text-ug-gold hover:border-gold-leaf/20 transition-colors tap-sm">
                    Archive Project
                  </button>
                )}
                {user.role === 'admin' && !project.two_edits && project.project_type !== 'photo' && project.payment_status === 'paid'
                  && !project.client_approved && (project.admin_status_id || 0) < 8 && (
                  // Split into Version A and B after ordering (backend app/version_split.py): this project keeps its
                  // editor and work as Version A; Version B opens unassigned, from the same order, at no extra cost.
                  <button type="button" data-testid="split-versions-btn"
                    onClick={async () => {
                      const ok = await confirm({
                        title: 'Split into Version A and Version B?',
                        message: 'This project becomes Version A and keeps its editor, drafts and chat. Version B opens as a new project from the same order, with no editor, at no extra cost to the client, as if they had ordered two versions. Their footage, notes and music go with it, and songs they already chose carry over. Assign Version B an editor next.',
                        confirmLabel: 'Split', cancelLabel: 'Keep one version',
                      });
                      if (!ok) return;
                      try {
                        const r = await api.post(`/projects/${project.id}/split-versions`, {});
                        const m = r.data.music || {};
                        toast.success('Version B opened. Assign it an editor.' + (m.failed?.length ? ` ${m.failed.length} music file(s) could not be copied: add them to Version B.` : ''));
                        window.location.href = `/dashboard?open=${r.data.version_b_id}`;
                      } catch (e) { toast.error(e.response?.data?.detail || 'Could not split this project'); }
                    }}
                    className="w-full flex items-center justify-center gap-2 py-2 rounded-md border border-gold-leaf/25 text-[10px] font-mono uppercase tracking-wider text-ug-gold/70 hover:text-ug-gold hover:border-gold-leaf/45 transition-colors tap-sm">
                    <Copy className="w-3 h-3" /> Split into Version A and B
                  </button>
                )}
                {user.role === 'admin' && project.two_edits_split && (
                  <button type="button" data-testid="undo-split-btn"
                    onClick={async () => {
                      const ok = await confirm({
                        title: 'Undo the split?',
                        message: 'Version B is deleted and this goes back to one project. Only possible while Version B has had no editor and no draft.',
                        confirmLabel: 'Undo split', cancelLabel: 'Keep both', destructive: true,
                      });
                      if (!ok) return;
                      try {
                        const r = await api.post(`/projects/${project.id}/undo-split-versions`);
                        toast.success('Split undone: back to one version');
                        window.location.href = `/dashboard?open=${r.data.version_a_id}`;
                      } catch (e) { toast.error(e.response?.data?.detail || 'Could not undo the split'); }
                    }}
                    className="w-full flex items-center justify-center gap-2 py-2 rounded-md border border-ug-border text-[10px] font-mono uppercase tracking-wider text-slate-500 hover:text-zinc-100 hover:border-ug-border-strong transition-colors tap-sm">
                    <RotateCcw className="w-3 h-3" /> Undo split
                  </button>
                )}
                {user.role === 'admin' && (
                  <>
                    {project.payment_status === 'paid' && (
                      <button type="button" onClick={() => setRefundMode('refund')} data-testid="refund-btn"
                        className="w-full flex items-center justify-center gap-2 py-2 rounded-md border border-ug-border text-[10px] font-mono uppercase tracking-wider text-slate-400 hover:text-zinc-100 hover:border-ug-border-strong transition-colors tap-sm">
                        <RotateCcw className="w-3 h-3" /> Refund
                      </button>
                    )}
                    {!project.cancelled && (
                      <button type="button" onClick={() => setRefundMode('cancel')} data-testid="cancel-order-btn" data-tone="warning"
                        className="w-full flex items-center justify-center gap-2 py-2 rounded-md border border-amber-500/30 text-[10px] font-mono uppercase tracking-wider text-amber-300/80 hover:text-amber-300 hover:border-amber-500/50 transition-colors tap-sm">
                        <Ban className="w-3 h-3" /> Cancel order
                      </button>
                    )}
                    <RefundDialog project={project} mode={refundMode || 'refund'} open={!!refundMode}
                      onOpenChange={(v) => { if (!v) setRefundMode(null); }}
                      onDone={() => { const wasCancel = refundMode === 'cancel'; onRefresh(); if (wasCancel) onClose(); }} />
                  </>
                )}
                {(user.role === 'admin' || user.role === 'staff') && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button data-testid="delete-project-btn" className="w-full flex items-center justify-center gap-2 py-2 rounded-md border border-velvet-burgundy/20 text-[10px] font-mono uppercase tracking-wider text-velvet-burgundy/60 hover:text-velvet-burgundy hover:border-velvet-burgundy/40 transition-colors tap-sm">
                        Delete Project
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-ug-raised border-ug-border-strong">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="text-zinc-100">Delete "{project.title}"?</AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-400">This will move the project to trash. It can be restored within 30 days, then it is gone for good.{project.payment_status === 'paid' && user.role === 'admin' && !project.cancelled ? ' Cancelling a paid order? Use Cancel order instead: it refunds, keeps the record, and takes the money out of revenue.' : ''}</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="border-ug-border-strong text-slate-400 hover:text-zinc-100">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={async () => { try { await api.put(`/projects/${project.id}/delete`); toast.success('Moved to trash'); onRefresh(); onClose(); } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); } }} className="bg-velvet-burgundy text-white hover:bg-velvet-burgundy/80">Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            )}

            {/* Client Info + Satisfaction + Redemption Cut — Admin only */}
            {(user.role === 'admin' || user.role === 'staff') && (
              <div className="pt-2 space-y-2">
                {project.client_satisfaction && (
                  <div className={`p-3 rounded-xl border ${project.client_satisfaction.score <= 2 ? 'bg-ug-status-red/5 border-ug-status-red/20' : project.client_satisfaction.score <= 3 ? 'bg-ug-gold/5 border-ug-gold/20' : 'bg-oxidized-teal/5 border-oxidized-teal/20'}`} data-testid="satisfaction-indicator">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-zinc-500">Client Feedback</span>
                      <div className="flex items-center gap-3">
                        {project.client_satisfaction.edit_rating > 0 && (
                          <div className="flex items-center gap-1">
                            <span className="text-[8px] text-zinc-500">Edit:</span>
                            <div className="flex gap-0.5">{[1,2,3,4,5].map(s => <Star key={s} className={`w-2.5 h-2.5 ${s <= project.client_satisfaction.edit_rating ? 'text-ug-gold fill-ug-gold' : 'text-zinc-700'}`} strokeWidth={1.5} />)}</div>
                          </div>
                        )}
                        <span className={`font-sans text-sm font-bold ${project.client_satisfaction.score <= 2 ? 'text-ug-status-red' : project.client_satisfaction.score <= 3 ? 'text-ug-gold' : 'text-oxidized-teal'}`}>Return: {project.client_satisfaction.score}/5</span>
                      </div>
                    </div>
                    {project.client_satisfaction.reasons?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-1">
                        {project.client_satisfaction.reasons.map(r => (
                          <span key={r} className="font-sans text-[8px] font-medium px-1.5 py-0.5 rounded bg-white/5 text-zinc-400">{r}</span>
                        ))}
                      </div>
                    )}
                    {project.client_satisfaction.comment && (
                      <p className="font-sans text-[10px] text-zinc-400 italic">"{project.client_satisfaction.comment}"</p>
                    )}
                    {project.client_satisfaction.score <= 2 && (
                      <p className="font-sans text-[9px] text-ug-status-red/80 mt-1 font-medium">Low satisfaction — consider a Redemption Cut</p>
                    )}
                  </div>
                )}
                <button onClick={() => { window.location.href = `/admin/redemptions?create=${project.id}`; }}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-dashed border-ug-status-red/30 text-ug-status-red hover:bg-ug-status-red/5 transition-colors" data-testid="offer-redemption-btn">
                  <Flame className="w-4 h-4" />
                  <span className="font-sans text-xs font-semibold">Offer Redemption Cut</span>
                </button>
              </div>
            )}
          </TabsContent>
          <TabsContent value="feedback" className="p-5 m-0 space-y-4 overflow-hidden">
            <ErrorBoundary fallback={tabErrorFallback}>
            {/* Client: review gated behind Approved status */}
            {user.role === 'client' && project.client_status_id < 8 && (
              <div className="mb-5 rounded-xl bg-ug-raised border border-ug-border p-5 text-center">
                <Lock className="w-6 h-6 text-slate-600 mx-auto mb-2" />
                <p className="text-[12px] text-slate-400">Feedback will be available once you approve the final edit.</p>
                <p className="text-[10px] text-slate-600 mt-1">Current status: {CLIENT_STATUSES[project.client_status_id]}</p>
              </div>
            )}

            {/* Feedback collected during project approval — no separate form needed */}
            {user.role === 'client' && project.client_satisfaction && (
              <div className="mb-5 p-4 rounded-md bg-oxidized-teal/[0.03] border border-oxidized-teal/15">
                <h3 className="font-sans text-[10px] font-semibold uppercase tracking-wider text-oxidized-teal/70 mb-2">Your Feedback</h3>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] text-zinc-500">Edit quality:</span>
                  <div className="flex gap-0.5">{[1,2,3,4,5].map(s => <Star key={s} className={`w-3 h-3 ${s <= (project.client_satisfaction.edit_rating || 0) ? 'text-ug-gold fill-ug-gold' : 'text-zinc-700'}`} strokeWidth={1.5} />)}</div>
                </div>
                {project.client_satisfaction.comment && <p className="text-[10px] text-zinc-400 italic mt-1">"{project.client_satisfaction.comment}"</p>}
                <p className="text-[9px] text-zinc-600 mt-1">Submitted when you approved this project</p>
              </div>
            )}

            {/* All: show reviews for this project */}
            {reviews.length > 0 && (
              <div className="mb-5">
                <h3 className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold/70 mb-3">Reviews for This Project</h3>
                <div className="space-y-2">
                  {reviews.map(r => (
                    <div key={r.id} className="p-3 rounded-xl bg-ug-raised border border-ug-border p-5">
                      <div className="flex items-center gap-2 mb-1">
                        {user.role !== 'editor' && <div className="flex gap-0.5">{[1,2,3,4,5].map(s => <Star key={s} className={`w-3 h-3 ${s <= r.rating ? 'text-ug-gold fill-gold-leaf' : 'text-slate-700'}`} />)}</div>}
                        {r.version_number > 0 && <span className="font-sans text-[9px] font-semibold text-oxidized-teal">V{r.version_number}</span>}
                        <span className="font-sans text-[9px] font-semibold text-slate-600 ml-auto">{formatEastern(r.created_at, { dateOnly: true })}</span>
                      </div>
                      <p className="text-[11px] text-zinc-100/70 leading-relaxed break-all">{r.feedback}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Editor/Admin: show ALL past feedback from this client — expandable with video */}
            {(user.role === 'editor' || user.role === 'admin' || user.role === 'staff') && clientReviews.length > 0 && (
              <div>
                <h3 className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold/70 mb-3">All Feedback from {project.client_name}</h3>
                <div className="space-y-2">
                  {clientReviews.map(r => (
                    <ClientReviewCard key={r.id} review={r} viewerRole={user.role} />
                  ))}
                </div>
              </div>
            )}
            {reviews.length === 0 && (user.role !== 'client') && clientReviews.length === 0 && (
              <p className="text-[11px] text-slate-600 py-4">No feedback yet</p>
            )}
            </ErrorBoundary>
          </TabsContent>

          {/* Client Profile Tab (admin, staff, billing, client) */}
          {(user.role === 'admin' || user.role === 'staff' || user.role === 'billing' || user.role === 'client') && (
            <TabsContent value="profile" className="m-0 p-5">
              <ErrorBoundary fallback={tabErrorFallback}>
              {(user.role === 'admin' || user.role === 'staff') && clientData?.weddings_per_year && (
                <div className="flex justify-between items-center py-1 border-b border-white/[0.03] mb-4">
                  <span className="font-sans text-[8px] font-medium uppercase tracking-wider text-ug-gold/50 w-20">Weddings/yr</span>
                  <span className="text-[10px] text-zinc-100/80">{clientData.weddings_per_year}</span>
                </div>
              )}
              <StyleProfileView profile={clientProfile} projectType={project.project_type} project={project} editUrl={
                user.role === 'client' ? `/style-profile${project.brand_name ? `?brand=${encodeURIComponent(project.brand_name)}` : ''}` :
                (user.role === 'admin' || user.role === 'staff') && project.client_id
                  ? `/style-profile?user_id=${project.client_id}${project.brand_name ? `&brand=${encodeURIComponent(project.brand_name)}` : ''}` : null
              } />
              </ErrorBoundary>
            </TabsContent>
          )}
        </ScrollArea>

        {/* Chat — pinned to bottom of drawer */}
        <div className="border-t border-ug-border shrink-0">
          {/* Client sees client chat only */}
          {user.role === 'client' && (
            <ChatThread projectId={project.id} system="client" compact defaultExpanded={expandChat === 'client'} readOnly={isClientChatLocked} onRead={onChatRead} clientName={project.client_name} />
          )}
          {/* Editor sees internal chat only */}
          {user.role === 'editor' && (
            <ChatThread projectId={project.id} system="internal" compact defaultExpanded={expandChat === 'internal'} onRead={onChatRead} pmOnDuty={pmOnDuty} />
          )}
          {/* Admin sees BOTH chats — client + internal */}
          {(user.role === 'admin' || user.role === 'staff' || user.role === 'billing') && (
            <div>
              <ChatThread projectId={project.id} system="client" compact defaultExpanded={expandChat === 'client'} readOnly={isClientChatLocked || user.role === 'billing'} onRead={onChatRead} clientName={project.client_name} />
              <div className="border-t border-ug-border">
                <ChatThread projectId={project.id} system="internal" compact defaultExpanded={expandChat === 'internal'} readOnly={isLocked || user.role === 'billing'} onRead={onChatRead} pmOnDuty={pmOnDuty} />
              </div>
            </div>
          )}
        </div>
      </Tabs>

      {/* Full-screen overlay — portaled to body to escape drawer's containing block */}
      {fullscreen && createPortal(
        <div className="fixed inset-0 z-[9999] bg-ug-deepest flex flex-col" data-testid="fullscreen-overlay">
          <div className="flex items-center justify-between px-6 py-3 border-b border-ug-border bg-ug-raised/80 backdrop-blur-sm shrink-0">
            <div className="flex items-center gap-3">
              {project.project_type === 'photo' ? <Image className="w-4 h-4 text-ug-gold" /> : <Film className="w-4 h-4 text-ug-gold" />}
              <h2 className="font-sans font-bold text-lg text-zinc-100">{project.title}</h2>
              <Badge className="font-sans text-[9px] font-semibold bg-white/5 text-slate-400 border-0 uppercase">
                {fullscreen === 'review' ? 'Video Review' : 'Review'}
              </Badge>
            </div>
            <button onClick={() => {
              // Capture fullscreen video time, then sync back to drawer video
              const fsVideo = document.querySelector('[data-testid="fullscreen-overlay"] video');
              const exitTime = fsVideo?.currentTime || 0;
              setFullscreen(null);
              // After closing fullscreen, seek the drawer video to the exit time
              requestAnimationFrame(() => {
                const drawerVideo = document.querySelector('[data-testid="review-tab"] video');
                if (drawerVideo && exitTime > 0) {
                  drawerVideo.currentTime = exitTime;
                }
              });
            }} data-testid="close-fullscreen-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[10px] font-mono uppercase tracking-wider text-slate-400 hover:text-zinc-100 hover:bg-white/5 transition-colors border border-ug-border">
              <X className="w-4 h-4" /> Exit Full Screen
            </button>
          </div>
          <div className="flex-1 overflow-auto">
            {fullscreen === 'review' && <VideoReview project={project} onRefresh={onRefresh} readOnly={isLocked} fullscreen initialTime={fullscreenInitialTime} />}
            {fullscreen === 'photos' && <PhotoReview project={project} onRefresh={onRefresh} />}
          </div>
        </div>,
        document.body
      )}

      {/* Record External Payment modal */}
      {showManualPayModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60" onClick={() => setShowManualPayModal(false)}>
          <div className="bg-ug-raised border border-ug-border-strong rounded-xl p-6 w-full max-w-md mx-4 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="manual-pay-modal">
            <h2 className="font-heading text-lg text-zinc-100 mb-1">Record External Payment</h2>
            <p className="font-sans text-[11px] text-slate-400 mb-4 leading-relaxed">This will mark this project as paid in the portal without processing a Stripe charge. Use this only if payment was already collected outside the portal.</p>
            <div className="space-y-2 mb-4 text-[11px]">
              <div className="flex justify-between"><span className="text-slate-500">Client</span><span className="text-zinc-200">{project.client_name}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Project</span><span className="text-zinc-200">{project.title}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Amount</span><span className="text-zinc-200">${project.total?.toFixed(2) || '0.00'}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Status</span><span className="text-ug-gold font-semibold uppercase">{project.payment_status || 'pending'}</span></div>
            </div>
            <label className="font-sans text-[10px] font-semibold uppercase tracking-wider text-slate-500 block mb-1">Note (optional)</label>
            <textarea value={manualPayNote} onChange={e => setManualPayNote(e.target.value)} placeholder="e.g., Paid via Stripe invoice INV-12345"
              className="w-full bg-ug-surface border border-ug-border rounded-md px-3 py-2 text-sm text-zinc-100 placeholder-slate-600 resize-none h-16 focus:outline-none focus:border-ug-gold/40" data-testid="manual-pay-note" />
            <div className="flex gap-2 mt-4">
              <Button variant="outline" onClick={() => { setShowManualPayModal(false); setManualPayNote(''); }} className="flex-1 border-ug-border text-slate-400 font-sans text-xs h-10">Cancel</Button>
              <Button disabled={manualPayProcessing} data-testid="confirm-manual-pay-btn" onClick={async () => {
                setManualPayProcessing(true);
                try {
                  await api.put(`/projects/${project.id}/record-external-payment`, { note: manualPayNote });
                  toast.success('Payment recorded successfully');
                  setShowManualPayModal(false); setManualPayNote('');
                  onRefresh();
                } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to record payment'); }
                finally { setManualPayProcessing(false); }
              }} className="flex-1 bg-oxidized-teal text-white hover:bg-oxidized-teal/90 font-sans text-xs font-semibold h-10">
                {manualPayProcessing ? 'Processing...' : 'Confirm — Mark as Paid'}
              </Button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
